// AdminHome — compact, info-rich, entity-themed dashboard.
//
// Design principles (locked-in):
//   1. ONE card per entity (Players / Agents / Bots / Rewards). Each
//      card is themed by that entity's colour and packs ALL its key
//      numbers — wallet, recharge inflow, withdrawal outflow, pending
//      counts — together. No more one-stat-per-card sprawl.
//   2. Inside each wallet card, the flow ARROWS are the colour of the
//      direction (green ↑ = money in, red ↓ = money out). This stays
//      consistent across Players / Agents / Bots so a fast scan tells
//      the whole story.
//   3. A date-range toggle (Today / Yesterday / This week / Last week)
//      sits in the hero — picking a range refetches the dashboard with
//      that bucket. Every "in this range" number on the page updates.
//   4. Things you can do (queues to clear, lists to inspect) are still
//      tappable — the entire card is a Link, with a bright chevron.
//
// Backend: /admin/reports/home-summary?range=today|yesterday|week|last_week|month|all
//          /admin/reports/agent-home-summary?range=…

import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { levelName } from '../lib/levelNames';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { GAMES as GAME_REGISTRY } from '@baaligames/shared-ui/games/registry';
import GameSettingsTile from '../components/GameSettingsTile';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';

import HeroStrip, { ActionPill, CircleIcon } from '@baaligames/shared-ui/admin/components/dashboard/HeroStrip';
import AdminAvatar from '@baaligames/shared-ui/admin/components/AdminAvatar';
import Icon from '@baaligames/shared-ui/components/Icon';

// 2026-05-16 (OperationsHub merge — Path C):
// The Operations Hub's 6 panels now drill into the Dashboard via the
// `?drill=…` URL state. Each panel is imported AS-IS — no rewrites,
// no duplication. Closing the drill returns the page to its compact
// dashboard view. Legacy /operations/* URLs redirect into this state.
import PlatformPanel from '../panels/operations/PlatformPanel';
import LeaderboardsPanel from '../panels/operations/LeaderboardsPanel';
import ActivityFeedPanel from '../panels/operations/ActivityFeedPanel';
import BotMatrixPanel from '../panels/operations/BotMatrixPanel';
import AuditLogPanel from '../panels/operations/AuditLogPanel';
import DlqPanel from '../panels/operations/DlqPanel';

// ─── Range toggle ──────────────────────────────────────────────────

type Range = 'today' | 'yesterday' | 'week' | 'last_week' | 'month' | 'last_month' | 'all';
const RANGE_OPTS: Array<{ key: Range; label: string }> = [
  { key: 'all',        label: 'Complete' },
  { key: 'last_month', label: 'Last month' },
  { key: 'month',      label: 'This month' },
  { key: 'last_week',  label: 'Last week' },
  { key: 'week',       label: 'This week' },
  { key: 'yesterday',  label: 'Yesterday' },
  { key: 'today',      label: 'Today' },
];

function RangeToggle({ value, onChange }: { value: Range; onChange: (v: Range) => void }) {
  // Mobile and desktop intentionally show the same 7 options in
  // different visual orderings — the user wants Today on the left
  // for both layouts, but the wrap shape is different on small screens.
  //
  //   Desktop (sm+):    Today · Yesterday · This week · Last week · This month · Last month · Complete   (1 row)
  //   Mobile top (4):   Complete · Last month · This month · Last week                                    (grid-cols-4)
  //   Mobile bottom (3): Today · Yesterday · This week                                                    (grid-cols-3)
  const desktopRow:  Array<{ key: Range; label: string }> = [
    { key: 'today',      label: 'Today' },
    { key: 'yesterday',  label: 'Yesterday' },
    { key: 'week',       label: 'This week' },
    { key: 'last_week',  label: 'Last week' },
    { key: 'month',      label: 'This month' },
    { key: 'last_month', label: 'Last month' },
    { key: 'all',        label: 'Complete' },
  ];
  const mobileTop4:   Array<{ key: Range; label: string }> = [
    { key: 'all',        label: 'Complete' },
    { key: 'last_month', label: 'Last month' },
    { key: 'month',      label: 'This month' },
    { key: 'last_week',  label: 'Last week' },
  ];
  const mobileBot3:   Array<{ key: Range; label: string }> = [
    { key: 'today',      label: 'Today' },
    { key: 'yesterday',  label: 'Yesterday' },
    { key: 'week',       label: 'This week' },
  ];
  // `whitespace-nowrap` prevents the multi-word labels ("This week",
  // "Last month", etc.) from wrapping onto two lines on desktop where
  // they have plenty of horizontal room. Mobile keeps the grid layout
  // which gives each button a fixed-width cell — wrapping there is
  // bounded by the cell anyway.
  const btnCls = (active: boolean) =>
    `w-full px-2 sm:px-3.5 py-1.5 rounded-full text-[12px] font-semibold transition whitespace-nowrap
     ${active
       ? 'bg-violet-600 text-white shadow-[0_2px_10px_rgba(139,92,246,0.4)]'
       : 'text-arena-700 hover:text-arena-900 dark:text-neon-200/70 dark:hover:text-white'}`;
  const Btn = (o: { key: Range; label: string }) => (
    <button key={o.key} onClick={() => onChange(o.key)} className={btnCls(value === o.key)}>
      {o.label}
    </button>
  );
  return (
    <div className="rounded-3xl p-1 bg-arena-100/60 dark:bg-arena-800/40
                    ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40">
      {/* mobile: 4-on-top + 3-on-bottom grids */}
      <div className="sm:hidden grid grid-cols-4 gap-1">{mobileTop4.map(Btn)}</div>
      <div className="sm:hidden grid grid-cols-3 gap-1 mt-1">{mobileBot3.map(Btn)}</div>
      {/* desktop: single inline-flex row, newest-first left to right */}
      <div className="hidden sm:inline-flex">{desktopRow.map(Btn)}</div>
    </div>
  );
}

// ─── Types ─────────────────────────────────────────────────────────

type AdminSummary = {
  range: Range;
  today_glance: { matches: number; revenue: number; signups: number; pending_total: number };
  money: {
    pending_recharges: number; pending_recharges_amount: number;
    pending_withdrawals: number; pending_withdrawals_amount: number;
    pending_agent_withdrawals: number; pending_agent_withdrawals_amount: number;
    pending_bulk: number;
    recharges_in_range_count: number;  recharges_in_range_amount: number;
    withdrawals_in_range_count: number; withdrawals_in_range_amount: number;
    last_24h_in: number; last_24h_out: number;
    recent: Array<{ kind: string; id: number; user_id: number; username: string;
                    amount: number; state: string; at: string }>;
  };
  players: {
    total: number; active_today: number; new_week: number; suspended: number; signups_week: number;
    // Profit-Stream additions (2026-05-16):
    via_referral: number;      // lifetime count of credited referrals
    with_offer_claims: number; // in-range distinct users who claimed offers
    // ── Players P&L inputs (2026-05-17) ──
    //   net admin P&L = stakes_amount − winnings_amount − refunds_amount − subsidies
    //   where subsidies = rewards.offers_paid_in_range_amount + rewards.referral_bonus_amount
    stakes_match_count: number;      // distinct matches in which real players bet
    stakes_amount: number;            // ₹ poured into pots by real players (positive)
    winnings_match_count: number;     // distinct matches with at least one real-player win
    winnings_amount: number;          // ₹ paid back to real players from pots
    refunds_count: number;            // refund ledger rows (cancelled matches)
    refunds_amount: number;           // ₹ refunded to real players
    top_active: Array<{ id: number; username: string; avatar_url: string|null; matches: number }>;
  };
  agents: {
    total: number; active: number; suspended: number; total_pool: number;
    topups_in_range_count: number; topups_in_range_amount: number;
    outflow_in_range_count: number; outflow_in_range_amount: number;
    // Profit-Stream additions:
    commissions_in_range_amount: number;   // signed by ledger convention; cost to platform
    by_level: Array<{ level: number; count: number }>;
    top_owners: Array<{ id: number; username: string; avatar_url: string|null; owned_count: number }>;
  };
  bots: {
    total: number; active: number; matches_today: number; total_wallet: number;
    // Profit-Stream addition: positive = bots gained, negative = drained
    wallet_net_in_range_amount: number;
  };
  games: Record<string, { matches_today: number; earnings_today: number }>;
  rewards: {
    pending_claims: number; week_paid_total: number; active_offers: number;
    // Profit-Stream additions:
    offers_paid_in_range_amount: number;  // alias of week_paid_total (range-bound)
    pending_optins: number;                // count of (offer × agent) pairs missing opt-in
    // ── Subsidies inputs (2026-05-17) ──
    // Referral bonus payouts in range. Counts as a subsidy alongside
    // offers_paid in the Players P&L calculation.
    referral_bonus_count: number;
    referral_bonus_amount: number;
  };
  // ── Phase 7.7 (2026-05-17) — admin's own books ──
  // Sourced from the double-entry ledgers wired in Phase 7.3/7.4
  // (migrations 080/081 + AdminWalletModel + AdminDepositModel).
  // Replaces the MyWalletModal placeholders.
  admin_wallet: {
    // Signed BIGINT — admin's LIFETIME P&L. Can go arbitrarily
    // negative (Override 1). Render with signedInr() + signColor().
    lifetime_balance: number;
    // Range aggregates from admin_wallet_ledger.
    in_range_net: number;        // signed sum
    in_range_inflow: number;     // >= 0 (sum of positive amounts)
    in_range_outflow: number;    // <= 0 (signed sum of negative amounts)
    in_range_entries: number;
  };
  admin_deposit: {
    // L1-only deposit liability (Override 2). Always ≥ 0. Rendered
    // NEUTRAL — this is money admin holds for L1 agents, NOT profit.
    liability: number;
    in_range_received: number;   // >= 0
    in_range_consumed: number;   // >= 0 (sum of |amount| consumed)
    in_range_refunded: number;   // >= 0 (sum of |amount| refunded)
    in_range_entries: number;
  };
};

type AgentSummary = {
  today_glance: { my_matches: number; my_earnings: number; pending_total: number };
  money: { pending_recharges: number; pending_recharges_amount: number;
           pending_withdrawals: number; pending_withdrawals_amount: number;
           my_pending_withdrawal: number; my_pending_withdrawal_amount: number;
           recharges_in_range_count: number;  recharges_in_range_amount: number;
           withdrawals_in_range_count: number; withdrawals_in_range_amount: number;
           recent: Array<{ kind: string; id: number; user_id: number; username: string;
                           amount: number; state: string; at: string }> };
  wallet: { pool: number; week_commissions: number; week_withdrawals: number;
            topups_in_range_count: number; topups_in_range_amount: number };
  players: { total: number; active_today: number; suspended: number;
             top_spenders: Array<{ id: number; username: string; avatar_url: string|null; week_spend: number }> };
};

// Phase 11F insights — combined snapshot of the analysis pages.
type LeaderboardRow = {
  agent_id: number; agent_username: string; agent_level: number;
  matches: number; true_net: string | number;
};
type FeedItem = {
  kind: 'match' | 'recharge' | 'partnership_change';
  at: string; agent_id: number | null; agent_username: string | null;
  match_id?: string; game_id?: string; pillar?: string; amount?: number;
  user_id?: number; state?: string;
  old_rate_pct?: string | number; new_rate_pct?: string | number; is_cascade?: boolean;
  ref_id: number;
};
type AdminInsights = {
  leaderboard: LeaderboardRow[];
  activity: FeedItem[];
  botMatrix: { platform_profit_window: number;
               // 2026-05-17 (Alternative Fee Policy follow-up): the
               // headline `platform_profit_window` now equals
               // `platform_match_profit_window + platform_fee_profit_window`.
               // Both component fields are surfaced separately so the
               // dashboard can render the breakdown next to the headline.
               platform_match_profit_window?: number;
               platform_fee_profit_window?:   number;
               total_active: number; total_bots: number;
               categories: Array<{ category: 'easy'|'medium'|'hard'; bot_net_p_l: number;
                                   match_count: number; kill_switch_enabled: boolean }> } | null;
  dlqRows: Array<{ id: number; match_id: string; attempts: number; failed_at: string }>;
  auditRows: Array<{ id: number; agent_id: number; agent_username: string | null;
                     pillar: string; game_id: string; old_rate_pct: string | null;
                     new_rate_pct: string; is_cascade: 0 | 1; changed_at: string }>;
  // 2026-05-16 Path C: PlatformPanel's three endpoints are pulled in
  // mini form so the Platform card on the InsightRibbon can render
  // True Net + worker health + deposits-at-risk without waiting for
  // the user to drill in.
  platformPnl: { platformTrueNet: number } | null;
  workerHealth: { healthy: boolean; queueDepth: number; dlqDepth: number } | null;
  depositsAtRisk: number;
};

// 2026-05-16 Path C: the 6 valid drill-in keys. The URL `?drill=…`
// state machines between these and `null` (no drill open).
type DrillKey = 'platform' | 'leaderboards' | 'activity' | 'bots' | 'audit' | 'dlq';
const DRILL_KEYS: DrillKey[] = ['platform', 'leaderboards', 'activity', 'bots', 'audit', 'dlq'];
function isDrillKey(s: string | null): s is DrillKey {
  return s != null && (DRILL_KEYS as string[]).includes(s);
}
const DRILL_TITLES: Record<DrillKey, { title: string; icon: string }> = {
  platform:     { title: 'Platform P&L',       icon: 'monitoring' },
  leaderboards: { title: 'Top earners',        icon: 'leaderboard' },
  activity:     { title: 'Live activity',      icon: 'dynamic_feed' },
  bots:         { title: 'Bot performance',    icon: 'precision_manufacturing' },
  audit:        { title: 'Rate-change log',    icon: 'history' },
  dlq:          { title: 'Settlement DLQ',     icon: 'warning' },
};
type AgentInsights = {
  leaderboard: LeaderboardRow[];
  activity: FeedItem[];
  depositLedger: Array<{ id: number; kind: string; amount: number; created_at: string }>;
};

const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN')}`;
// Signed INR for profit/loss displays. Prepends '+' for positive,
// '−' (U+2212 minus, not ASCII hyphen — typographically correct) for
// negative, nothing for zero. Used on the Summary Cards + modal headlines.
const signedInr = (n: number) => {
  const v = n || 0;
  if (v > 0) return `+₹${v.toLocaleString('en-IN')}`;
  if (v < 0) return `−₹${Math.abs(v).toLocaleString('en-IN')}`;
  return '₹0';
};

// Universal sign → color helper. The user's color rule (locked 2026-05-16):
//   profit (n > 0)  → green  (text-emerald-*)
//   loss   (n < 0)  → red    (text-rose-*)
//   zero            → neutral (text-arena-*)
// Every signed-amount display on the admin dashboard pipes through
// this so red/green is consistently the loss/profit signal — never
// hijacked by entity tone, category color, or any other framing.
const signColor = (n: number) =>
  n > 0 ? 'text-emerald-700 dark:text-emerald-300' :
  n < 0 ? 'text-rose-700 dark:text-rose-300' :
          'text-arena-700 dark:text-neon-200/80';

// 2026-05-17 (Games row merge): GAME_META used to drive the plain
// "Games" stat tiles at the mid-dashboard. After merging into the
// colourful GameSettingsTile, label/icon come straight from the
// shared game registry — this constant is gone. The previous bottom
// "Game settings" section + its dedicated `GameTile` helper are gone
// for the same reason.

export default function AdminHome() {
  const { admin } = useAdminAuth();
  if (!admin) return null;
  // Agent role can't reach the admin SPA; always the admin dashboard.
  return <AdminDashboard />;
}

// ─── Module-level cache (2026-05-20) ───────────────────────────────
//
// AdminHome fires 9 endpoints per range value on mount: the main
// `home-summary` query + 8 parallel insights endpoints. Without
// caching, navigating away and back (Players → Home → Players →
// Home) re-fires all 9 every time. With a small TTL cache anchored
// at module scope, repeat visits inside the TTL window render from
// memory and cost zero requests.
//
// TTL is intentionally short (20 s) — admin data should feel live,
// not stale. After 20 s the next mount refetches; until then,
// memory hit. The cache is keyed by `range` so switching between
// today / week / month doesn't serve the wrong slice.
//
// `cacheVersion` lets imperative refreshes (e.g. after an admin
// posts an action that mutates dashboard data) bust the cache.
// Set via `bustAdminHomeCache()` — exported below.
const CACHE_TTL_MS = 20_000;
type SummaryCacheEntry  = { data: AdminSummary;  at: number };
type InsightsCacheEntry = { data: AdminInsights; at: number };
type AgentSumCacheEntry = { data: AgentSummary;  at: number };
type AgentInsCacheEntry = { data: AgentInsights; at: number };
const summaryCache:  Record<string, SummaryCacheEntry>  = {};
const insightsCache: Record<string, InsightsCacheEntry> = {};
const agentSumCache: Record<string, AgentSumCacheEntry> = {};
const agentInsCache: Record<string, AgentInsCacheEntry> = {};
export function bustAdminHomeCache() {
  for (const k of Object.keys(summaryCache))  delete summaryCache[k];
  for (const k of Object.keys(insightsCache)) delete insightsCache[k];
  for (const k of Object.keys(agentSumCache)) delete agentSumCache[k];
  for (const k of Object.keys(agentInsCache)) delete agentInsCache[k];
}

// ─── ADMIN ─────────────────────────────────────────────────────────

type SummaryModalKey = 'players' | 'agents' | 'bots' | 'wallet';

function AdminDashboard() {
  const { admin } = useAdminAuth();
  const [data, setData] = useState<AdminSummary | null>(null);
  const [err, setErr]   = useState<string | null>(null);
  const [range, setRange] = useState<Range>('today');
  const [insights, setInsights] = useState<AdminInsights | null>(null);
  // 2026-05-16: Summary-Card → EntityModal pattern. null = no modal.
  const [summaryModal, setSummaryModal] = useState<SummaryModalKey | null>(null);
  // Bumped by the testing-phase switch: both loaders below refetch (cache busted first).
  const [tick, setTick] = useState(0);

  // 2026-05-20 — Cache-first load. If the module-level summaryCache
  // has a fresh entry for this range, use it AND skip the network
  // call. Otherwise fetch and populate the cache.
  const load = useCallback((r: Range) => {
    const cached = summaryCache[r];
    if (cached && Date.now() - cached.at < CACHE_TTL_MS) {
      setData(cached.data);
      return;
    }
    adminApi.get<AdminSummary>(`/admin/reports/home-summary?range=${r}`)
      .then((d) => { summaryCache[r] = { data: d, at: Date.now() }; setData(d); })
      .catch((e) => setErr((e as ApiError).message || 'Failed to load'));
  }, []);
  useEffect(() => { load(range); }, [range, load, tick]);

  // Phase 11F + 2026-05-16 (Path C merge): insights for the
  // InsightRibbon. Eight endpoints in parallel — the original five
  // (leaderboard, activity, bot-matrix, dlq, audit) plus three from
  // PlatformPanel (platform-pnl, settlement-health, deposits).
  //
  // 2026-05-20 — Same cache-first pattern as `load` above. Eight
  // endpoints is the heaviest "shotgun blast" in the SPA; caching it
  // for 20 s means the typical Home → click-away → back-to-Home
  // navigation costs zero network roundtrips.
  useEffect(() => {
    const cached = insightsCache[range];
    if (cached && Date.now() - cached.at < CACHE_TTL_MS) {
      setInsights(cached.data);
      return;
    }
    let cancelled = false;
    Promise.allSettled([
      adminApi.get<{ rows: any[] }>('/admin/leaderboards/agents?window=7d&pillar=all&limit=5'),
      adminApi.get<{ items: any[] }>('/admin/activity-feed?window=24h&kind=all&limit=5'),
      adminApi.get<any>('/admin/bot-matrix?window=7d'),
      adminApi.get<{ rows: any[] }>('/admin/settlement/dlq'),
      adminApi.get<{ rows: any[] }>('/admin/audit/rate-changes?limit=5'),
      adminApi.get<{ platformTrueNet: number }>('/admin/dashboard/platform-pnl?window=7d'),
      adminApi.get<{ healthy: boolean; queueDepth: number; dlqDepth: number }>('/admin/settlement/health'),
      adminApi.get<{ rows: Array<{ status: string }> }>('/admin/dashboard/deposits'),
    ]).then((res) => {
      if (cancelled) return;
      const lb  = res[0].status === 'fulfilled' ? res[0].value.rows : [];
      const af  = res[1].status === 'fulfilled' ? res[1].value.items : [];
      const bm  = res[2].status === 'fulfilled' ? res[2].value : null;
      const dlq = res[3].status === 'fulfilled' ? res[3].value.rows : [];
      const au  = res[4].status === 'fulfilled' ? res[4].value.rows : [];
      const ppnl = res[5].status === 'fulfilled' ? res[5].value : null;
      const wh   = res[6].status === 'fulfilled' ? res[6].value : null;
      const depAtRisk = res[7].status === 'fulfilled'
        ? res[7].value.rows.filter((r) => ['breach', 'suspended', 'locked', 'warning'].includes(r.status)).length
        : 0;
      const next: AdminInsights = {
        leaderboard: lb, activity: af, botMatrix: bm, dlqRows: dlq, auditRows: au,
        platformPnl: ppnl, workerHealth: wh, depositsAtRisk: depAtRisk,
      };
      insightsCache[range] = { data: next, at: Date.now() };
      setInsights(next);
    });
    return () => { cancelled = true; };
  }, [range, tick]);

  // 2026-05-16 Path C: URL state machine for the drill-in panel.
  // `?drill=platform` → PlatformPanel mounts in Zone 3, etc.
  // `null` → ribbon-only view; lower sections (Games · Activity ·
  // Game settings) render normally below the ribbon.
  const [searchParams, setSearchParams] = useSearchParams();
  const drillParam = searchParams.get('drill');
  const drill: DrillKey | null = isDrillKey(drillParam) ? drillParam : null;
  const openDrill = useCallback((k: DrillKey) => {
    setSearchParams((sp) => { const next = new URLSearchParams(sp); next.set('drill', k); return next; });
  }, [setSearchParams]);
  const closeDrill = useCallback(() => {
    setSearchParams((sp) => { const next = new URLSearchParams(sp); next.delete('drill'); return next; });
  }, [setSearchParams]);

  // Auto-scroll the drill-in into view when it opens (mobile UX —
  // otherwise the panel appears below the fold and the user doesn't
  // realise it loaded).
  const drillRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (drill && drillRef.current) {
      drillRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [drill]);

  return (
    <Container>
      {/* 2026-05-18 — Identity masking: greet by display_name when set,
          fall back to username only as a last resort (self view). */}
      <Greeting username={(((admin?.first_name || '') + ' ' + (admin?.last_name || '')).trim() || admin?.username)} subtitle="Welcome back" right={
        <RangeToggle value={range} onChange={setRange} />
      } />

      {/* HeroStrip — 2026-05-16 UI overhaul: action pills + kebab
          removed (zero-purpose surface clutter). Hero is now just the
          headline metric + flow arrows + 4 snapshot tiles. */}
      <HeroStrip
        label="Platform inflow"
        value={data?.money.recharges_in_range_amount ?? 0}
        prefix="₹"
        subtitle={data ? <>
          <span className="text-emerald-600 dark:text-emerald-300 font-semibold">↑ {inr(data.money.recharges_in_range_amount)}</span>
          <span className="text-arena-400 dark:text-neon-200/35 mx-2">·</span>
          <span className="text-rose-600 dark:text-rose-300 font-semibold">↓ {inr(data.money.withdrawals_in_range_amount)}</span>
          <span className="text-arena-400 dark:text-neon-200/35 mx-2">·</span>
          <span>{rangeLabel(range)}</span>
        </> : '—'}
        right={data ? <SnapshotMini items={[
          { label: 'Matches',     value: data.today_glance.matches.toLocaleString('en-IN'),         icon: 'sports_esports' },
          { label: 'New signups', value: data.today_glance.signups.toLocaleString('en-IN'),         icon: 'person_add' },
          { label: 'Active',      value: data.players.active_today.toLocaleString('en-IN'),         icon: 'group' },
          { label: 'Pending',     value: data.today_glance.pending_total.toLocaleString('en-IN'),
            icon: 'hourglass_top',
            tone: data.today_glance.pending_total > 0 ? 'warn' : 'ok' },
        ]} /> : null}
      />

      {/* ════════════════════════════════════════════════════════════
          Profit Stream Cards — 2026-05-16 (Profit-Stream Dashboard).
          Each card shows ₹ profit/cost for one stream of platform
          activity (NOT entity counts). Eye-tracking design:
            • Headline = signed amount, green for net positive, rose
              for net negative or cost
            • Sub = supporting context (volume, count, breakdown)
            • Optional badge = "X pending" or warning state
            • Tap card → EntityModal with comprehensive deep-dive
         ════════════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3 px-2 lg:px-4">
        {/* Card 1: Players — REAL admin P&L from player activity.
            2026-05-17 corrected formula:
              stakes − winnings − refunds − subsidies = net admin P&L
            (subsidies = offers_paid + referral_bonus)
            Recharges & withdrawals are EXCLUDED here — they're wallet
            flows, not P&L (admin holds player money, doesn't own it).
            They surface inside the modal as a separate "Wallet flow"
            section labelled as informational. */}
        {(() => {
          const stakes    = data ? data.players.stakes_amount   : 0;
          const winnings  = data ? data.players.winnings_amount : 0;
          const refunds   = data ? data.players.refunds_amount  : 0;
          const subsidies = data ? data.rewards.offers_paid_in_range_amount + data.rewards.referral_bonus_amount : 0;
          const playerNet = stakes - winnings - refunds - subsidies;
          const pendingTotal = data ? data.money.pending_recharges + data.money.pending_withdrawals : 0;
          return (
            <SummaryCard
              tone="player"
              icon="group"
              label="Players"
              headline={data ? signedInr(playerNet) : '—'}
              headlineSigned={data ? playerNet : 0}
              sub={data
                ? `${data.players.stakes_match_count.toLocaleString('en-IN')} game${data.players.stakes_match_count === 1 ? '' : 's'} · ${inr(stakes)} staked`
                : '—'}
              badge={pendingTotal > 0 ? `${pendingTotal} pending` : null}
              onClick={() => setSummaryModal('players')}
            />
          );
        })()}

        {/* Card 2: Agents — COMMISSION COST.
            Negative number, rose tone. The platform pays agents for
            distribution; this card surfaces that as an honest expense
            line so admin can see it as a controllable lever.
            Edge case (2026-05-16 audit): when zero commissions are
            paid in range, render neutral '₹0' instead of '−₹0' — a
            negative sign on zero is visual nonsense. */}
        {(() => {
          const commissionCost = data ? Math.abs(data.agents.commissions_in_range_amount) : 0;
          const hasCommissions = commissionCost > 0;
          return (
            <SummaryCard
              tone="agent"
              icon="support_agent"
              label="Agents"
              headline={!data ? '—' : hasCommissions ? `−${inr(commissionCost)}` : '₹0'}
              headlineSigned={hasCommissions ? -commissionCost : 0}
              sub={data
                ? `${data.agents.active.toLocaleString('en-IN')} active · ${inr(data.agents.total_pool)} in pools`
                : '—'}
              badge={data && data.money.pending_agent_withdrawals > 0
                ? `${data.money.pending_agent_withdrawals} pending`
                : null}
              onClick={() => setSummaryModal('agents')}
            />
          );
        })()}

        {/* Card 3: Bots — platform profit from bot ecosystem.
            Positive = bots earned for platform (won matches + collected fees).
            Negative = bots drained (lost to players, net of fees collected).
            2026-05-17: headline now includes platform_fee_bot_share. */}
        {(() => {
          const botProfit  = insights?.botMatrix?.platform_profit_window ?? 0;
          const matchPart  = insights?.botMatrix?.platform_match_profit_window;
          const feePart    = insights?.botMatrix?.platform_fee_profit_window;
          const hasSplit   = matchPart !== undefined && feePart !== undefined;
          const draining = insights?.botMatrix?.categories.some(
            (c) => c.bot_net_p_l > 0 && c.kill_switch_enabled
          );
          // Sub-line: when the backend supplied the breakdown, show
          // "match + fees" so admins immediately see WHY the headline
          // is what it is. Falls back to the original active-count
          // line on older deploys.
          const subLine = hasSplit
            ? `Match ${signedInr(matchPart)} · Fees ${signedInr(feePart)}`
            : (data
                ? `${data.bots.active.toLocaleString('en-IN')} of ${data.bots.total.toLocaleString('en-IN')} active`
                : '—');
          return (
            <SummaryCard
              tone="bot"
              icon="smart_toy"
              label="Bots"
              headline={insights?.botMatrix ? signedInr(botProfit) : '—'}
              headlineSigned={botProfit}
              sub={subLine}
              badge={draining ? '⚠ Draining' : null}
              badgeTone="danger"
              onClick={() => setSummaryModal('bots')}
            />
          );
        })()}

        {/* Card 4: My Wallet — LIVE (Phase 7.7, 2026-05-17).
            Headline is admin's LIFETIME P&L straight from
            admin_wallet.balance_points. Sourced from the double-entry
            ledger; can be arbitrarily negative (Override 1).
            Sub-line shows the in-range net and current deposit
            liability so admin sees "this period" + "what I'm holding"
            alongside the lifetime headline. Tap → full breakdown modal.
            Badge: only flagged for net-loss (already-green headline
            conveys profit; a positive-tone badge would be redundant). */}
        {(() => {
          const lifetime = data ? data.admin_wallet.lifetime_balance : 0;
          const inRange  = data ? data.admin_wallet.in_range_net    : 0;
          const deposit  = data ? data.admin_deposit.liability      : 0;
          const subLine = data
            ? `${signedInr(inRange)} ${rangeLabel(range).toLowerCase()} · ${inr(deposit)} held in deposit`
            : '—';
          return (
            <SummaryCard
              tone="wallet"
              icon="account_balance_wallet"
              label="My Wallet"
              headline={data ? signedInr(lifetime) : '₹—'}
              headlineSigned={lifetime}
              sub={subLine}
              badge={lifetime < 0 ? 'Net loss' : null}
              badgeTone="danger"
              onClick={() => setSummaryModal('wallet')}
            />
          );
        })()}
      </div>

      {/* ════════════════════════════════════════════════════════════
          ZONE 2 (Path C, 2026-05-16) — InsightRibbon.
          Always visible. Six compact cards = the entry points that
          used to live as the Operations Hub tabs. P&L first per the
          architectural decision (most critical financial signal).
         ════════════════════════════════════════════════════════════ */}
      <SectionTitle title="Platform analytics" subtitle="Tap any card to drill into the full panel" />
      <TestingPhaseBar onChanged={() => { bustAdminHomeCache(); setTick((t) => t + 1); }} />
      <InsightRibbon data={insights} drill={drill} onOpen={openDrill} />

      {/* ════════════════════════════════════════════════════════════
          ZONE 3 (Path C) — DrillPanel.
          Conditionally mounts ONE Operations panel inline when the
          `?drill=…` query param is set. AnimatePresence handles the
          soft slide-in / slide-out. When open, the lower sections
          (Games, Activity, Game settings) hide themselves — mobile
          users never face the 19k-pixel stacked-everything scroll
          we analysed before greenlighting Path C.
         ════════════════════════════════════════════════════════════ */}
      <AnimatePresence mode="wait">
        {drill && (
          <div ref={drillRef} key={drill}>
            <DrillPanel drill={drill} onClose={closeDrill} />
          </div>
        )}
      </AnimatePresence>

      {/* The dashboard's lower body — only renders when no drill is
          open. Saves the user from scrolling past 6 sections of
          unrelated content to get back to the ribbon when they're
          actively investigating one panel. */}
      {!drill && <>
        {/* ════════════════════════════════════════════════════════
            Games row — 2026-05-17 MERGE.
            Previously two separate sections rendered the same 5
            games: an at-a-glance "Games" stat row + a colourful
            "Game settings" shortcut row at the very bottom. Same
            data, doubled vertical space. Merged into a single row
            here at the mid-dashboard slot: gradient tile + per-game
            match/earnings data + click-to-configure. The bottom
            "Game settings" section is gone — its purpose now lives
            inside every game card on this row. */}
        <SectionTitle title="Games" subtitle={`Matches and platform fee · ${rangeLabel(range)} · tap to configure`} />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 px-2 lg:px-4">
          {GAME_REGISTRY.map((g) => (
            <GameSettingsTile
              key={g.id}
              game={g}
              matches={data?.games?.[g.id]?.matches_today ?? 0}
              earnings={data?.games?.[g.id]?.earnings_today ?? 0}
            />
          ))}
        </div>

        <SectionTitle title="Activity" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 px-2 lg:px-4 mb-4">
          <Card title="Top players" icon="trending_up" subtitle={rangeLabel(range)}>
            <TopList rows={(data?.players.top_active ?? []).slice(0, 5).map((p, i) => ({
              id: p.id, avatar_url: p.avatar_url, username: p.username,
              // 2026-05-18 — Pass first/last so TopList composes
              // display name. TopList renders display_name primary;
              // username is only used as a fallback for the avatar
              // initials seed.
              first_name: (p as { first_name?: string|null }).first_name ?? null,
              last_name:  (p as { last_name?:  string|null }).last_name  ?? null,
              metric: `${p.matches} matches`, rank: i + 1, to: `/people/players/${p.id}`,
            }))} empty="No matches in this range." />
          </Card>
          <Card title="Top agents" icon="emoji_events" subtitle="By owned players">
            {/* 2026-05-18 — Peer rows: render display_name only.
                If the backend doesn't yet return first/last for these
                rows we pass undefined and TopList falls through to a
                dash placeholder (per the privacy directive — never
                leak @username for peer agents). */}
            <TopList isPeer rows={(data?.agents.top_owners ?? []).slice(0, 5).map((a, i) => ({
              id: a.id, avatar_url: a.avatar_url, username: a.username,
              first_name: (a as { first_name?: string|null }).first_name ?? null,
              last_name:  (a as { last_name?:  string|null }).last_name  ?? null,
              metric: `${a.owned_count} player${a.owned_count === 1 ? '' : 's'}`, rank: i + 1, to: `/people/agents/${a.id}`,
            }))} empty="No active agents yet." />
          </Card>
          <Card title="Recent transactions" icon="receipt_long" subtitle="Last 5">
            <TxList rows={data?.money.recent ?? []} />
          </Card>
        </div>
      </>}

      {err && <ErrorBanner msg={err} />}

      {/* 2026-05-16: Entity Summary Modals. Lifted to the bottom of the
          Container so they overlay the entire dashboard surface
          regardless of where the user scrolled before tapping a card. */}
      <AnimatePresence>
        {summaryModal === 'players' && (
          <PlayersSummaryModal data={data} onClose={() => setSummaryModal(null)} />
        )}
        {summaryModal === 'agents' && (
          <AgentsSummaryModal data={data} insights={insights} onClose={() => setSummaryModal(null)} />
        )}
        {summaryModal === 'bots' && (
          <BotsSummaryModal data={data} insights={insights} onClose={() => setSummaryModal(null)} />
        )}
        {summaryModal === 'wallet' && (
          <MyWalletModal data={data} range={range} onClose={() => setSummaryModal(null)} />
        )}
      </AnimatePresence>
    </Container>
  );
}

// ─── AGENT ─────────────────────────────────────────────────────────

function rangeLabel(r: Range) {
  switch (r) {
    case 'today':      return 'Today';
    case 'yesterday':  return 'Yesterday';
    case 'week':       return 'This week';
    case 'last_week':  return 'Last week';
    case 'month':      return 'This month';
    case 'last_month': return 'Last month';
    case 'all':        return 'Complete';
  }
}

function Container({ children }: { children: ReactNode }) {
  return (
    <div className="max-w-screen-2xl mx-auto px-2 sm:px-3 lg:px-6 py-3 lg:py-4 space-y-4">
      {children}
    </div>
  );
}

function Greeting({ username, subtitle, right }: { username?: string; subtitle: string; right?: ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
      className="px-2 lg:px-4 pt-3 lg:pt-2 flex items-center justify-between gap-3 flex-wrap"
    >
      <div className="text-xl sm:text-2xl lg:text-3xl font-display font-semibold tracking-tight">
        <span className="text-arena-600 dark:text-neon-200/70 font-normal">{subtitle}, </span>
        <span className="text-neutral-900 dark:text-white">{username || 'there'}</span>
      </div>
      {right}
    </motion.div>
  );
}

function SectionTitle({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="px-2 lg:px-4 -mb-1 flex items-baseline gap-2">
      <h2 className="text-[14px] font-semibold text-neutral-900 dark:text-white tracking-tight">{title}</h2>
      {subtitle && <p className="text-[11px] text-arena-600 dark:text-neon-200/55">· {subtitle}</p>}
    </div>
  );
}

function Card({ title, icon, subtitle, children, to }: {
  title: string; icon: string; subtitle?: string; children: ReactNode; to?: string;
}) {
  const inner = (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="rounded-2xl p-4
                 bg-white dark:bg-arena-700/40
                 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none"
    >
      <header className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon name={icon} size={15} className="text-arena-600 dark:text-neon-200/70" />
          <h3 className="text-[13px] font-semibold text-neutral-900 dark:text-white truncate">{title}</h3>
        </div>
        {to && <Icon name="chevron_right" size={15} className="text-arena-400 dark:text-neon-200/35 shrink-0" />}
      </header>
      {subtitle && <p className="text-[11px] text-arena-600 dark:text-neon-200/55 -mt-1 mb-2">{subtitle}</p>}
      {children}
    </motion.div>
  );
  if (to) return <Link to={to} className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/50 rounded-2xl">{inner}</Link>;
  return inner;
}

// ── Mini snapshot tiles for the hero's right column

function SnapshotMini({ items }: {
  items: Array<{ label: string; value: string; icon: string; tone?: 'ok' | 'warn' }>;
}) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      {items.map((it, i) => (
        <motion.div
          key={it.label}
          initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, delay: 0.04 * i }}
          className="rounded-xl px-3 py-2
                     bg-white dark:bg-arena-700/40
                     ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                     shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none"
        >
          <div className="flex items-center gap-1.5 mb-0.5">
            <Icon name={it.icon} size={12} className={`
              ${it.tone === 'warn'
                ? 'text-amber-600 dark:text-amber-300'
                : 'text-violet-600 dark:text-violet-300'}`} />
            <span className="text-[10.5px] text-arena-600 dark:text-neon-200/70 truncate">{it.label}</span>
          </div>
          <div className="text-[15px] sm:text-[17px] font-display font-extrabold text-neutral-900 dark:text-white tabular-nums leading-tight">
            {it.value}
          </div>
        </motion.div>
      ))}
    </div>
  );
}

// ── WalletCard — the entity-themed packed card. Same shape for
//    Players / Agents / Bots / Rewards. Tone drives the colour accents.

type Tone = 'player' | 'agent' | 'bot' | 'reward';
type FlowRow    = { dir: 'in' | 'out'; label: string; count: number; amount: number };
type PendingRow = { label: string; count: number; to: string };
type StatRow    = { label: string; value: string };

const TONE: Record<Tone, {
  iconBg: string; iconText: string; ring: string; chip: string; chipText: string; accent: string;
}> = {
  player: {
    iconBg: 'bg-cyan-50 dark:bg-cyan-400/15',
    iconText: 'text-cyan-700 dark:text-cyan-300',
    ring: 'ring-cyan-200 dark:ring-cyan-400/30',
    chip: 'bg-cyan-50 dark:bg-cyan-400/10',
    chipText: 'text-cyan-800 dark:text-cyan-200',
    accent: 'text-cyan-700 dark:text-cyan-300',
  },
  agent: {
    iconBg: 'bg-emerald-50 dark:bg-emerald-400/15',
    iconText: 'text-emerald-700 dark:text-emerald-300',
    ring: 'ring-emerald-200 dark:ring-emerald-400/30',
    chip: 'bg-emerald-50 dark:bg-emerald-400/10',
    chipText: 'text-emerald-800 dark:text-emerald-200',
    accent: 'text-emerald-700 dark:text-emerald-300',
  },
  bot: {
    iconBg: 'bg-amber-50 dark:bg-amber-400/15',
    iconText: 'text-amber-700 dark:text-amber-300',
    ring: 'ring-amber-200 dark:ring-amber-400/30',
    chip: 'bg-amber-50 dark:bg-amber-400/10',
    chipText: 'text-amber-800 dark:text-amber-200',
    accent: 'text-amber-700 dark:text-amber-300',
  },
  reward: {
    iconBg: 'bg-violet-50 dark:bg-violet-400/15',
    iconText: 'text-violet-700 dark:text-violet-300',
    ring: 'ring-violet-200 dark:ring-violet-400/30',
    chip: 'bg-violet-50 dark:bg-violet-400/10',
    chipText: 'text-violet-800 dark:text-violet-200',
    accent: 'text-violet-700 dark:text-violet-300',
  },
};

function WalletCard({ tone, icon, title, headline, headlineLabel, flow = [], pending = [], stats = [], to }: {
  tone: Tone;
  icon: string;
  title: string;
  headline: string;
  headlineLabel: string;
  flow?: FlowRow[];
  pending?: PendingRow[];
  stats?: StatRow[];
  to: string;
}) {
  const t = TONE[tone];
  // Batch1 fix #5/N1: chips need their own navigation target — but they live
  // INSIDE the outer card <Link>, so a nested <Link> would be invalid HTML
  // and the click would propagate to the parent. Use programmatic navigate
  // + stopPropagation so each chip lands on its own destination (e.g.
  // "Pending withdrawals" → /finance/players, not the parent /users).
  const navigate = useNavigate();
  return (
    <Link
      to={to}
      className={`
        block rounded-2xl p-4
        bg-white dark:bg-arena-700/40
        ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 ring-1 ${t.ring}
        shadow-[0_2px_10px_-4px_rgba(0,0,0,0.06)] dark:shadow-none
        hover:-translate-y-0.5 transition
        focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/50
      `}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`grid place-items-center w-9 h-9 rounded-xl ${t.iconBg} ${t.iconText}`}>
            <Icon name={icon} size={18} filled />
          </span>
          <h3 className="text-[13.5px] font-semibold text-neutral-900 dark:text-white truncate">{title}</h3>
        </div>
        <Icon name="chevron_right" size={16} className="text-arena-400 dark:text-neon-200/35 shrink-0" />
      </div>

      {/* Headline number */}
      <div className={`text-[26px] sm:text-3xl font-display font-extrabold tabular-nums leading-none tracking-tight ${t.accent}`}>
        {headline}
      </div>
      <div className="text-[11px] text-arena-600 dark:text-neon-200/70 mt-1 mb-3">{headlineLabel}</div>

      {/* Flow (green ↑ + red ↓) */}
      {flow.length > 0 && (
        <ul className="space-y-1.5 mb-3 border-t border-arena-200/40 dark:border-arena-700/40 pt-2.5">
          {flow.map((f) => (
            <li key={f.label} className="flex items-center justify-between gap-3">
              <span className="flex items-center gap-2 min-w-0">
                <span className={`grid place-items-center w-6 h-6 rounded-full
                  ${f.dir === 'in'
                    ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-400/15 dark:text-emerald-300'
                    : 'bg-rose-50 text-rose-600 dark:bg-rose-400/15 dark:text-rose-300'}`}>
                  <Icon name={f.dir === 'in' ? 'arrow_upward' : 'arrow_downward'} size={13} />
                </span>
                <span className="text-[12px] text-arena-700 dark:text-neon-200/80 truncate">{f.label}</span>
              </span>
              <span className="shrink-0 flex items-baseline gap-2">
                {f.count > 0 && (
                  <span className="text-[11px] text-arena-600 dark:text-neon-200/70 tabular-nums">{f.count}</span>
                )}
                <span className={`text-[12.5px] font-semibold tabular-nums
                  ${f.dir === 'in'
                    ? 'text-emerald-700 dark:text-emerald-300'
                    : 'text-rose-700 dark:text-rose-300'}`}>
                  {inr(f.amount)}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Pending — chips with violet/amber accent + count */}
      {pending.length > 0 && (
        <ul className="flex flex-wrap gap-1.5 mb-3">
          {pending.map((p) => {
            const chipCls = `
              inline-flex items-center gap-1 rounded-full px-2 py-0.5
              text-[11px] font-medium
              ${p.count > 0
                ? 'bg-amber-50 text-amber-800 dark:bg-amber-400/15 dark:text-amber-200'
                : 'bg-arena-100/60 text-arena-600 dark:bg-arena-700/50 dark:text-neon-200/55'}
            `;
            const chipBody = (
              <>
                <Icon name="hourglass_top" size={11} />
                {p.label}
                <span className="font-bold tabular-nums ml-1">{p.count}</span>
              </>
            );
            // Batch1 fix #5/N1: if a chip declares its own `to`, render
            // it as a button that intercepts the click, prevents the outer
            // card-link from firing, and navigates to the chip's target.
            return (
              <li key={p.label}>
                {p.to ? (
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(p.to!); }}
                    className={`${chipCls} hover:ring-1 hover:ring-amber-300 dark:hover:ring-amber-400/40 cursor-pointer`}
                  >
                    {chipBody}
                  </button>
                ) : (
                  <span className={chipCls}>{chipBody}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Stats — small key/value grid at the bottom */}
      {stats.length > 0 && (
        <div className="grid grid-cols-3 gap-2 border-t border-arena-200/40 dark:border-arena-700/40 pt-2.5">
          {stats.map((s) => (
            <div key={s.label} className="min-w-0">
              <div className="text-[10.5px] text-arena-600 dark:text-neon-200/55 truncate">{s.label}</div>
              <div className="text-[12.5px] font-semibold text-neutral-900 dark:text-white tabular-nums truncate">{s.value}</div>
            </div>
          ))}
        </div>
      )}
    </Link>
  );
}

// 2026-05-17: GameTile (plain violet stat tile) was removed when the
// per-game row was merged into the colourful GameSettingsTile. See
// the Games-row block in AdminDashboard for the replacement.

// ── Top list — players or agents with avatars + metric chip + rank

function TopList({ rows, empty, isPeer = false }: {
  rows: Array<{
    id: number; avatar_url: string|null; username: string;
    // 2026-05-18 — Optional structured name fields. When present and
    // composing to a non-empty string, the row renders by display_name
    // and DROPS the @username prefix. When isPeer=true we render the
    // dash placeholder instead of any fallback (peer privacy).
    first_name?: string|null; last_name?: string|null;
    metric: string; rank: number; to?: string;
  }>;
  empty: string;
  /** True when the rows are PEER agents (display_name only, no @username fallback). */
  isPeer?: boolean;
}) {
  if (rows.length === 0) {
    return <div className="text-center text-[12px] text-arena-500 dark:text-neon-200/45 py-5">{empty}</div>;
  }
  return (
    <ul className="space-y-1">
      {rows.map((r) => {
        const composed = `${(r.first_name || '').trim()} ${(r.last_name || '').trim()}`.trim();
        // Players show @username (operational); peer agents show
        // display_name (or '—' when first/last unfilled).
        const label = composed
          ? composed
          : (isPeer ? '—' : `@${r.username}`);
        return (
          <li key={r.id}>
            <Link
              to={r.to || '#'}
              className="flex items-center gap-3 px-2 py-1.5 -mx-2 rounded-lg
                         hover:bg-arena-50 dark:hover:bg-arena-700/60 transition"
            >
              <span className="w-4 text-[11px] font-bold text-arena-500 dark:text-neon-200/45 tabular-nums">
                {r.rank}
              </span>
              <AdminAvatar url={r.avatar_url} username={r.username || '—'} size="xs" />
              <div className="min-w-0 flex-1">
                <div className={`text-[12.5px] truncate ${composed ? 'text-neutral-900 dark:text-white' : (isPeer ? 'text-neutral-500 dark:text-neon-200/55' : 'text-neutral-900 dark:text-white')}`}>
                  {label}
                </div>
              </div>
              <span className="shrink-0 text-[12px] font-semibold text-violet-700 dark:text-violet-300 tabular-nums">
                {r.metric}
              </span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function TxList({ rows }: {
  rows: Array<{
    kind: string; id: number;
    username: string;
    // 2026-05-18 — Structured identity for display_name composition.
    first_name?: string | null;
    last_name?:  string | null;
    amount: number; state: string; at: string;
  }>;
}) {
  if (rows.length === 0) {
    return <div className="text-center text-[12px] text-arena-500 dark:text-neon-200/45 py-5">No transactions yet.</div>;
  }
  return (
    <ul className="space-y-1">
      {rows.slice(0, 5).map((r) => {
        const display = `${(r.first_name || '').trim()} ${(r.last_name || '').trim()}`.trim() || '—';
        return (
          <li key={`${r.kind}-${r.id}`}>
            <Link to="/people/players/requests" className="flex items-center gap-3 px-2 py-1.5 -mx-2 rounded-lg hover:bg-arena-50 dark:hover:bg-arena-700/60 transition">
              <span className={`grid place-items-center w-6 h-6 rounded-full
                ${r.kind === 'withdrawal'
                  ? 'bg-rose-50 text-rose-600 dark:bg-rose-400/15 dark:text-rose-300'
                  : 'bg-emerald-50 text-emerald-600 dark:bg-emerald-400/15 dark:text-emerald-300'}`}>
                <Icon name={r.kind === 'withdrawal' ? 'arrow_downward' : 'arrow_upward'} size={13} />
              </span>
              <div className="min-w-0 flex-1">
                <div className={`text-[12.5px] truncate ${display === '—' ? 'text-neutral-500 dark:text-neon-200/55' : 'text-neutral-900 dark:text-white'}`}>{display}</div>
                <div className="text-[10.5px] text-arena-600 dark:text-neon-200/50 truncate capitalize">{r.kind} · {r.state}</div>
              </div>
              <div className={`text-[12px] font-semibold tabular-nums shrink-0
                              ${r.kind === 'withdrawal'
                                ? 'text-rose-700 dark:text-rose-300'
                                : 'text-emerald-700 dark:text-emerald-300'}`}>
                {r.kind === 'withdrawal' ? '-' : '+'}{inr(r.amount)}
              </div>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function ErrorBanner({ msg }: { msg: string }) {
  return (
    <div className="mx-2 lg:mx-4 mt-4 rounded-2xl bg-rose-50 dark:bg-rose-500/10 ring-1 ring-inset ring-rose-200/60 dark:ring-rose-400/30 text-rose-800 dark:text-rose-200 px-4 py-3 text-sm">
      {msg}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// ENTITY SUMMARY CARDS + MODALS (2026-05-16 UI overhaul)
//
// Pattern: a small SummaryCard on the dashboard surface shows the
// at-a-glance count + one supporting metric + an optional "pending"
// badge. Tapping the card opens a full EntityModal with the wallet
// flow rows, pending chips, stats grid, and top lists that used to
// sprawl across the dashboard inside the bulky WalletCards.
//
// Four entities: Players, Agents, Bots, My Wallet (admin). The first
// three pull from the same /admin/reports/home-summary data the rest
// of the dashboard uses — zero extra fetches. The fourth (My Wallet)
// is a UI shell only; the backend ledger is a follow-up phase.
// ════════════════════════════════════════════════════════════════════

type SummaryTone = 'player' | 'agent' | 'bot' | 'wallet';
const SUMMARY_TONES: Record<SummaryTone, {
  iconBg: string; iconText: string; ring: string; accent: string; bgGlow: string;
}> = {
  player: {
    iconBg:  'bg-cyan-50 dark:bg-cyan-400/15',
    iconText:'text-cyan-700 dark:text-cyan-300',
    ring:    'ring-cyan-200/60 dark:ring-cyan-400/30',
    accent:  'text-cyan-700 dark:text-cyan-300',
    bgGlow:  'group-hover:bg-cyan-50/40 dark:group-hover:bg-cyan-400/[0.04]',
  },
  agent: {
    iconBg:  'bg-emerald-50 dark:bg-emerald-400/15',
    iconText:'text-emerald-700 dark:text-emerald-300',
    ring:    'ring-emerald-200/60 dark:ring-emerald-400/30',
    accent:  'text-emerald-700 dark:text-emerald-300',
    bgGlow:  'group-hover:bg-emerald-50/40 dark:group-hover:bg-emerald-400/[0.04]',
  },
  bot: {
    iconBg:  'bg-amber-50 dark:bg-amber-400/15',
    iconText:'text-amber-700 dark:text-amber-300',
    ring:    'ring-amber-200/60 dark:ring-amber-400/30',
    accent:  'text-amber-700 dark:text-amber-300',
    bgGlow:  'group-hover:bg-amber-50/40 dark:group-hover:bg-amber-400/[0.04]',
  },
  wallet: {
    iconBg:  'bg-violet-50 dark:bg-violet-400/15',
    iconText:'text-violet-700 dark:text-violet-300',
    ring:    'ring-violet-200/60 dark:ring-violet-400/30',
    accent:  'text-violet-700 dark:text-violet-300',
    bgGlow:  'group-hover:bg-violet-50/40 dark:group-hover:bg-violet-400/[0.04]',
  },
};

/**
 * SummaryCard — Profit-Stream tile.
 *
 * 2026-05-17 mobile-layout fix: at 2-col mobile widths (~190px each),
 * a badge ("2 PENDING", "LIFETIME SOON", "DRAINING") competing on the
 * same row as the label was forcing the label to truncate as
 * "PLA...", "AGE...", "BO...", "M." — making cards visually
 * unidentifiable. Fix: badge moves to its OWN row (right-aligned),
 * label gets full width always. Label also switched from uppercase
 * tracking-wider 11px to sentence-case 13px for legibility.
 *
 * Visual layout (top → bottom):
 *   Row 1:  [icon]  Players            ← full-width label, never truncates
 *   Row 2:                  [2 PENDING] ← badge, right-aligned, own row
 *   Row 3:  ₹47,250                     ← signed ₹ headline, sign-coloured
 *   Row 4:  4 players · 78 matches      ← supporting sub-line
 *   Row 5:                          →   ← tap affordance
 *
 * The `headlineSigned` prop drives the headline color (>0 emerald,
 * <0 rose, ==0 neutral). The `headline` prop is the formatted display
 * string so the caller controls exact formatting.
 */
function SummaryCard({
  tone, icon, label, headline, headlineSigned, sub, badge, badgeTone = 'warn', onClick,
}: {
  tone: SummaryTone;
  icon: string;
  label: string;
  headline: string;
  headlineSigned: number;
  sub: string;
  badge?: string | null;
  /** Visual severity of the badge.
   *  'warn'   → amber  (default; "X pending", "Lifetime soon")
   *  'danger' → rose   (e.g. "Draining" — losing platform money)
   *  'info'   → violet (e.g. neutral status info) */
  badgeTone?: 'warn' | 'danger' | 'info';
  onClick: () => void;
}) {
  const BADGE_TONES = {
    warn:   'bg-amber-100 text-amber-700 dark:bg-amber-400/15 dark:text-amber-300',
    danger: 'bg-rose-100 text-rose-700 dark:bg-rose-400/15 dark:text-rose-300',
    info:   'bg-violet-100 text-violet-700 dark:bg-violet-400/15 dark:text-violet-300',
  } as const;
  const t = SUMMARY_TONES[tone];
  // Color the headline by SIGN — eye lands on color before reading number.
  const headlineColor =
    headlineSigned > 0 ? 'text-emerald-700 dark:text-emerald-300' :
    headlineSigned < 0 ? 'text-rose-700 dark:text-rose-300' :
                         'text-arena-600 dark:text-neon-200/70';

  return (
    <motion.button
      onClick={onClick}
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }}
      transition={{ type: 'spring', stiffness: 380, damping: 26 }}
      className={`group relative text-left rounded-2xl p-3.5 sm:p-4 flex flex-col gap-1.5
                  bg-white dark:bg-arena-700/40
                  ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                  hover:ring-2 hover:${t.ring}
                  shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/50
                  transition`}>
      {/* Hover halo */}
      <div aria-hidden className={`absolute inset-0 rounded-2xl ${t.bgGlow} transition pointer-events-none`} />

      {/* Row 1: icon + label — gets the full card width.
          Sentence-case + no tracking-wider so "Players" / "Agents" /
          "Bots" / "My Wallet" all render in their natural width. */}
      <div className="relative flex items-center gap-2 min-w-0">
        <span className={`grid place-items-center w-8 h-8 rounded-xl ${t.iconBg} ${t.iconText} shrink-0`}>
          <Icon name={icon} size={15} filled />
        </span>
        <span className="text-[12.5px] sm:text-[13px] font-bold text-arena-800 dark:text-neon-200/85 truncate">
          {label}
        </span>
      </div>

      {/* Row 2: badge (conditional) — its own row.
          Severity-toned: warn=amber, danger=rose, info=violet. */}
      {badge && (
        <div className="relative flex justify-start -mt-0.5">
          <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9.5px] font-bold uppercase tracking-wider ${BADGE_TONES[badgeTone]}`}>
            {badge}
          </span>
        </div>
      )}

      {/* Row 3: headline ₹ — sign-driven color, tabular-nums */}
      <div className={`relative text-[20px] sm:text-2xl lg:text-[28px] font-display font-extrabold tabular-nums leading-none tracking-tight truncate ${headlineColor}`}>
        {headline}
      </div>

      {/* Row 4: supporting sub-line */}
      <div className="relative text-[10.5px] sm:text-[11px] text-arena-600 dark:text-neon-200/60 truncate">
        {sub}
      </div>

      {/* Row 5: tap affordance — subtle until hover */}
      <div className="relative mt-0.5 flex items-center justify-end opacity-50 group-hover:opacity-100 transition">
        <Icon name="arrow_forward" size={13} className={t.accent} />
      </div>
    </motion.button>
  );
}

/**
 * EntityModal — generic modal shell.
 *
 * Mobile (< sm): bottom-sheet style — full width, sliding up from the
 * bottom, rounded only at the top corners, max-h 90vh so the user can
 * still see a sliver of the dashboard behind it (visual anchor).
 *
 * Desktop (sm+): centered modal, max-w-lg, rounded all corners.
 *
 * Sticky header with icon + title + close. Scrollable body. Optional
 * sticky footer with a primary CTA (the "Open the full page" link).
 *
 * Locks body scroll while open, closes on Esc + backdrop click.
 */
function EntityModal({
  tone, icon, title, subtitle, primaryAction, children, onClose,
}: {
  tone: SummaryTone;
  icon: string;
  title: string;
  subtitle?: string;
  primaryAction?: { label: string; to?: string; onClick?: () => void; icon?: string };
  children: ReactNode;
  onClose: () => void;
}) {
  const t = SUMMARY_TONES[tone];

  // Body scroll lock — restored on unmount even if close handler is
  // called from anywhere (Esc, backdrop, button).
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Esc-to-close.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      className="fixed inset-0 z-[90] bg-black/55 backdrop-blur-sm
                 flex items-end sm:items-start sm:items-center justify-center p-0 sm:p-4 overflow-y-auto"
    >
      <motion.div
        initial={{ y: '6%', opacity: 0, scale: 0.98 }}
        animate={{ y: 0,      opacity: 1, scale: 1 }}
        exit={{    y: '6%', opacity: 0, scale: 0.98 }}
        transition={{ type: 'spring', stiffness: 360, damping: 30 }}
        className="w-full sm:max-w-lg
                   bg-white dark:bg-arena-800
                   ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                   rounded-t-3xl sm:rounded-2xl
                   shadow-[0_-12px_40px_-8px_rgba(0,0,0,0.25)] dark:shadow-[0_-12px_40px_-8px_rgba(0,0,0,0.6)] sm:shadow-2xl
                   max-h-[90vh] sm:max-h-[85vh] flex flex-col overflow-hidden"
      >
        {/* Mobile grabber — visual cue this is a bottom-sheet */}
        <div className="sm:hidden flex justify-center pt-2.5 pb-1">
          <div className="w-10 h-1 rounded-full bg-arena-200 dark:bg-arena-600/70" />
        </div>

        {/* Sticky header */}
        <header className="px-4 py-3 flex items-center gap-3 border-b ring-arena-200/40 dark:ring-arena-600/40 shrink-0">
          <span className={`grid place-items-center w-9 h-9 rounded-xl ${t.iconBg} ${t.iconText} shrink-0`}>
            <Icon name={icon} size={18} filled />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="text-[15px] sm:text-base font-bold text-neutral-900 dark:text-white truncate">{title}</h2>
            {subtitle && (
              <p className="text-[11px] text-arena-600 dark:text-neon-200/60 truncate">{subtitle}</p>
            )}
          </div>
          <button onClick={onClose} aria-label="Close"
                  className="grid place-items-center w-8 h-8 rounded-full
                             hover:bg-arena-100/60 dark:hover:bg-arena-700/70
                             text-arena-600 dark:text-neon-200/70 transition">
            <Icon name="close" size={18} />
          </button>
        </header>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {children}
        </div>

        {/* Optional sticky footer */}
        {primaryAction && (
          <footer className="px-4 py-3 border-t ring-arena-200/40 dark:ring-arena-600/40 shrink-0 bg-white dark:bg-arena-800">
            {primaryAction.to
              ? <Link to={primaryAction.to}
                      className={`w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl
                                  text-[13px] font-bold
                                  bg-violet-600 hover:bg-violet-500 active:bg-violet-700 text-white
                                  shadow-[0_2px_10px_rgba(139,92,246,0.4)]
                                  transition active:scale-95`}>
                  {primaryAction.icon && <Icon name={primaryAction.icon} size={15} />}
                  {primaryAction.label} <Icon name="arrow_forward" size={14} />
                </Link>
              : <button onClick={primaryAction.onClick}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl
                                   text-[13px] font-bold
                                   bg-violet-600 hover:bg-violet-500 active:bg-violet-700 text-white
                                   shadow-[0_2px_10px_rgba(139,92,246,0.4)]
                                   transition active:scale-95">
                  {primaryAction.icon && <Icon name={primaryAction.icon} size={15} />}
                  {primaryAction.label}
                </button>
            }
          </footer>
        )}
      </motion.div>
    </motion.div>
  );
}

// ─── Modal-body primitives ─────────────────────────────────────────

/** Big-number block at the top of a modal. */
function ModalHeadline({ value, label, tone }: {
  value: string; label: string; tone: SummaryTone;
}) {
  const t = SUMMARY_TONES[tone];
  return (
    <div className="text-center py-2">
      <div className={`text-3xl sm:text-4xl font-display font-extrabold tabular-nums leading-none tracking-tight ${t.accent}`}>
        {value}
      </div>
      <div className="text-[11.5px] text-arena-600 dark:text-neon-200/60 mt-1.5">{label}</div>
    </div>
  );
}

/** 2x or 3x stat grid. */
// Phase 7.7 (2026-05-17): added 'positive' / 'negative' / 'neutral'
// tones so the My Wallet modal can render P&L breakdowns with the
// platform-wide sign-color convention (green +, red −, neutral 0).
// The pre-existing 'ok' / 'warn' tones are kept for backward compat;
// 'ok' renders identically to 'neutral', 'warn' stays amber.
type StatTone = 'ok' | 'warn' | 'positive' | 'negative' | 'neutral';
function ModalStatsGrid({ stats }: { stats: Array<{ label: string; value: string; tone?: StatTone }> }) {
  const cols = stats.length >= 4 ? 'grid-cols-4' : stats.length === 3 ? 'grid-cols-3' : 'grid-cols-2';
  const toneClass = (t?: StatTone) => {
    switch (t) {
      case 'positive': return 'text-emerald-700 dark:text-emerald-300';
      case 'negative': return 'text-rose-700 dark:text-rose-300';
      case 'warn':     return 'text-amber-700 dark:text-amber-300';
      case 'neutral':
      case 'ok':
      default:         return 'text-neutral-900 dark:text-white';
    }
  };
  return (
    <div className={`grid ${cols} gap-2`}>
      {stats.map((s) => (
        <div key={s.label} className="rounded-xl px-3 py-2
                                       bg-arena-50/60 dark:bg-arena-700/30
                                       ring-1 ring-inset ring-arena-200/40 dark:ring-arena-600/30">
          <div className="text-[10px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60 font-bold truncate">{s.label}</div>
          <div className={`text-[15px] font-bold tabular-nums truncate ${toneClass(s.tone)}`}>{s.value}</div>
        </div>
      ))}
    </div>
  );
}

/** Flow row (green ↑ / red ↓) — money in/out. */
function ModalFlowRow({ dir, label, count, amount }: {
  dir: 'in' | 'out'; label: string; count?: number; amount: number;
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl
                    bg-arena-50/60 dark:bg-arena-700/30
                    ring-1 ring-inset ring-arena-200/40 dark:ring-arena-600/30">
      <div className="flex items-center gap-2 min-w-0">
        <span className={`grid place-items-center w-7 h-7 rounded-full shrink-0
          ${dir === 'in'
            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300'
            : 'bg-rose-100 text-rose-700 dark:bg-rose-400/15 dark:text-rose-300'}`}>
          <Icon name={dir === 'in' ? 'arrow_upward' : 'arrow_downward'} size={14} />
        </span>
        <span className="text-[12.5px] text-arena-800 dark:text-neon-200/85 truncate">{label}</span>
      </div>
      <div className="text-right shrink-0">
        {count !== undefined && count > 0 && (
          <div className="text-[10px] text-arena-600 dark:text-neon-200/55 tabular-nums">{count} tx</div>
        )}
        <div className={`text-[13.5px] font-bold tabular-nums
          ${dir === 'in'
            ? 'text-emerald-700 dark:text-emerald-300'
            : 'text-rose-700 dark:text-rose-300'}`}>
          {inr(amount)}
        </div>
      </div>
    </div>
  );
}

/** Pending chip — clickable, lands on the request queue. */
function ModalPendingChip({ label, count, to, onClose }: {
  label: string; count: number; to: string; onClose: () => void;
}) {
  if (count === 0) return null;
  return (
    <Link to={to} onClick={onClose}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full
                     bg-amber-50 hover:bg-amber-100 dark:bg-amber-400/15 dark:hover:bg-amber-400/25
                     ring-1 ring-inset ring-amber-200/60 dark:ring-amber-400/30
                     text-amber-800 dark:text-amber-200 text-[11.5px] font-semibold transition">
      <Icon name="hourglass_top" size={12} />
      <span>{label}</span>
      <span className="font-bold tabular-nums">{count}</span>
      <Icon name="arrow_forward" size={11} />
    </Link>
  );
}

/** Compact top list — same shape as TopList but tuned for modal width. */
function ModalTopList({ rows, empty, onClose }: {
  rows: Array<{ id: number; avatar_url: string | null; username: string; metric: string; rank: number; to: string }>;
  empty: string;
  onClose: () => void;
}) {
  if (rows.length === 0) {
    return <div className="text-center text-[12px] text-arena-500 dark:text-neon-200/45 py-3">{empty}</div>;
  }
  return (
    <ul className="space-y-1">
      {rows.map((r) => (
        <li key={r.id}>
          <Link to={r.to} onClick={onClose}
                className="flex items-center gap-2.5 px-2 py-1.5 -mx-2 rounded-lg
                           hover:bg-arena-50 dark:hover:bg-arena-700/60 transition">
            <span className="w-5 text-[11px] font-bold text-arena-500 dark:text-neon-200/45 tabular-nums">{r.rank}</span>
            <AdminAvatar url={r.avatar_url} username={r.username} size="xs" />
            <div className="min-w-0 flex-1">
              <div className="text-[12.5px] text-neutral-900 dark:text-white truncate">@{r.username}</div>
            </div>
            <span className="shrink-0 text-[12px] font-semibold text-violet-700 dark:text-violet-300 tabular-nums">{r.metric}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/** Section label inside a modal body. */
function ModalSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="text-[10px] font-bold uppercase tracking-wider text-arena-500 dark:text-neon-200/60">
        {title}
      </div>
      {children}
    </div>
  );
}

// ─── Profit-Stream modal primitives ────────────────────────────────

/** A single big signed-amount headline with breakdown bars beneath.
 *  Zero is rendered in neutral (matches the locked color rule —
 *  green/rose are reserved for actual profit/loss signals). */
function ProfitHeadline({ value, label }: {
  value: number; label: string; tone: SummaryTone;
}) {
  return (
    <div className="text-center py-2">
      <div className={`text-3xl sm:text-4xl font-display font-extrabold tabular-nums leading-none tracking-tight ${signColor(value)}`}>
        {signedInr(value)}
      </div>
      <div className="text-[11.5px] text-arena-600 dark:text-neon-200/60 mt-1.5">{label}</div>
    </div>
  );
}

/** Profit-breakdown row: +recharges − withdrawals − offers = net.
 *  When `amount === 0` we drop the − sign and render in neutral
 *  (an unsigned ₹0 in green/rose was the source of the "−₹0 in red"
 *  visual nonsense — a minus sign on a zero is meaningless). */
function ProfitBreakdownRow({ sign, label, amount, hint }: {
  sign: '+' | '−'; label: string; amount: number; hint?: string;
}) {
  const isZero = amount === 0;
  const isPositive = sign === '+';
  const iconBg = isZero
    ? 'bg-arena-100 text-arena-700 dark:bg-arena-700/50 dark:text-neon-200/65'
    : isPositive
      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300'
      : 'bg-rose-100 text-rose-700 dark:bg-rose-400/15 dark:text-rose-300';
  const valueColor = isZero
    ? 'text-arena-700 dark:text-neon-200/80'
    : isPositive
      ? 'text-emerald-700 dark:text-emerald-300'
      : 'text-rose-700 dark:text-rose-300';
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl
                    bg-arena-50/60 dark:bg-arena-700/30
                    ring-1 ring-inset ring-arena-200/40 dark:ring-arena-600/30">
      <div className="flex items-center gap-2 min-w-0">
        <span className={`grid place-items-center w-6 h-6 rounded-full shrink-0 text-[12px] font-black ${iconBg}`}>
          {sign}
        </span>
        <div className="min-w-0">
          <div className="text-[12.5px] text-arena-800 dark:text-neon-200/85 truncate">{label}</div>
          {hint && <div className="text-[10px] text-arena-500 dark:text-neon-200/45 truncate">{hint}</div>}
        </div>
      </div>
      <div className={`text-[13.5px] font-bold tabular-nums shrink-0 ${valueColor}`}>
        {isZero ? '₹0' : (sign === '−' ? '−' : '') + inr(amount)}
      </div>
    </div>
  );
}

/** Net-result row at the end of a breakdown.
 *  Zero is neutral (matches the locked color rule). */
function ProfitNetRow({ amount, label }: { amount: number; label: string }) {
  const ringCls = amount > 0
    ? 'bg-emerald-50/70 dark:bg-emerald-400/[0.08] ring-emerald-200/60 dark:ring-emerald-400/30'
    : amount < 0
      ? 'bg-rose-50/70 dark:bg-rose-400/[0.08] ring-rose-200/60 dark:ring-rose-400/30'
      : 'bg-arena-50/70 dark:bg-arena-700/40 ring-arena-200/60 dark:ring-arena-600/40';
  return (
    <div className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl ring-1 ring-inset ${ringCls}`}>
      <span className="text-[11.5px] font-bold uppercase tracking-wider text-arena-800 dark:text-neon-200/85">{label}</span>
      <span className={`text-[15px] font-bold tabular-nums ${signColor(amount)}`}>
        {signedInr(amount)}
      </span>
    </div>
  );
}

/** Cross-cut metric row — for engagement statistics.
 *
 *  Color convention (2026-05-16 audit):
 *    - signedAmount > 0  → emerald  (profit / inflow)
 *    - signedAmount < 0  → rose     (loss / outflow)
 *    - signedAmount === 0 OR undefined → neutral (state / count)
 *
 *  Caller passes `signedAmount` whenever the displayed `value`
 *  represents a P&L event so the user can read profit-vs-loss at a
 *  glance. Counts, balances, and other neutral metrics omit it. */
function CrossCutRow({ icon, label, value, hint, signedAmount }: {
  icon: string;
  label: string;
  value: string;
  hint?: string;
  signedAmount?: number;
}) {
  const color = signedAmount === undefined || signedAmount === 0
    ? 'text-neutral-900 dark:text-white'
    : signedAmount > 0
      ? 'text-emerald-700 dark:text-emerald-300'
      : 'text-rose-700 dark:text-rose-300';
  return (
    <div className="flex items-center justify-between gap-3 py-2 px-1">
      <div className="flex items-center gap-2 min-w-0">
        <Icon name={icon} size={14} className="text-arena-500 dark:text-neon-200/50 shrink-0" />
        <div className="min-w-0">
          <div className="text-[12px] text-arena-800 dark:text-neon-200/80 truncate">{label}</div>
          {hint && <div className="text-[10px] text-arena-500 dark:text-neon-200/45 truncate">{hint}</div>}
        </div>
      </div>
      <span className={`text-[13px] font-bold tabular-nums shrink-0 ${color}`}>{value}</span>
    </div>
  );
}

// ─── Players modal — Profit-Stream redesign ────────────────────────

function PlayersSummaryModal({ data, onClose }: { data: AdminSummary | null; onClose: () => void }) {
  // ── Profit inputs (the REAL admin P&L from player activity) ──
  const stakes        = data?.players.stakes_amount        ?? 0;
  const stakesMatches = data?.players.stakes_match_count   ?? 0;
  const winnings      = data?.players.winnings_amount      ?? 0;
  const winMatches    = data?.players.winnings_match_count ?? 0;
  const refunds       = data?.players.refunds_amount       ?? 0;
  const refundsCount  = data?.players.refunds_count        ?? 0;
  const offersPaid    = data?.rewards.offers_paid_in_range_amount ?? 0;
  const referralPaid  = data?.rewards.referral_bonus_amount       ?? 0;
  const subsidies     = offersPaid + referralPaid;
  const playerNet     = stakes - winnings - refunds - subsidies;

  // ── Wallet flow inputs (informational — admin holds, doesn't own) ──
  const recharges    = data?.money.recharges_in_range_amount  ?? 0;
  const withdrawals  = data?.money.withdrawals_in_range_amount ?? 0;
  const netWalletPourIn = recharges - withdrawals;

  const engagementPct = data && data.players.active_today > 0
    ? Math.round((data.players.with_offer_claims / data.players.active_today) * 100)
    : 0;

  return (
    <EntityModal
      tone="player"
      icon="group"
      title="Players"
      subtitle="Real admin P&L from player activity"
      primaryAction={{ label: 'Open Players list', to: '/people/players/list', icon: 'group' }}
      onClose={onClose}
    >
      {/* ════════════════════════════════════════════════════════════
          SECTION A — REAL ADMIN PROFIT (range)
          stakes − winnings − refunds − subsidies = net admin P&L.
          This is what admin actually earns/loses from player matches.
         ════════════════════════════════════════════════════════════ */}
      <ProfitHeadline value={playerNet} label="Net admin P&L from player activity (range)" tone="player" />

      <ModalSection title="How the net was built">
        <div className="space-y-1.5">
          <ProfitBreakdownRow sign="+" label="Stakes (games played)"
            amount={stakes}
            hint={`${stakesMatches.toLocaleString('en-IN')} game${stakesMatches === 1 ? '' : 's'} · poured into pots`} />
          <ProfitBreakdownRow sign="−" label="Player winnings"
            amount={winnings}
            hint={`${winMatches.toLocaleString('en-IN')} game${winMatches === 1 ? '' : 's'} won by players · paid out from pots`} />
          {refunds > 0 && (
            <ProfitBreakdownRow sign="−" label="Refunds (cancelled matches)"
              amount={refunds}
              hint={`${refundsCount.toLocaleString('en-IN')} refund${refundsCount === 1 ? '' : 's'} · stake returned, fee not earned`} />
          )}
          <ProfitBreakdownRow sign="−" label="Subsidies (offers + referrals)"
            amount={subsidies}
            hint={`offers ${inr(offersPaid)} + referral bonuses ${inr(referralPaid)}`} />
          <ProfitNetRow amount={playerNet} label="Net admin P&L" />
        </div>
      </ModalSection>

      {/* ════════════════════════════════════════════════════════════
          SECTION B — WALLET FLOW (range, INFORMATIONAL)
          Recharges and withdrawals are BALANCE flows — admin holds
          player money but doesn't own it. Surfaced here for
          situational awareness (how much liquidity is pooled in
          player wallets) but explicitly NOT counted in P&L.
         ════════════════════════════════════════════════════════════ */}
      <ModalSection title="Wallet flow (range) — balance only, not P&L">
        <div className="rounded-xl ring-1 ring-inset ring-arena-200/40 dark:ring-arena-600/30 bg-arena-50/40 dark:bg-arena-700/20 px-3 py-1 divide-y divide-arena-200/40 dark:divide-arena-600/30">
          <CrossCutRow icon="arrow_upward" label="Recharges credited"
            value={inr(recharges)}
            hint={`${data?.money.recharges_in_range_count.toLocaleString('en-IN') ?? 0} transactions · money players put in`} />
          <CrossCutRow icon="arrow_downward" label="Withdrawals paid out"
            value={inr(withdrawals)}
            hint={`${data?.money.withdrawals_in_range_count.toLocaleString('en-IN') ?? 0} transactions · money players took out`} />
          <CrossCutRow icon="account_balance_wallet" label="Net wallet pour-in"
            value={signedInr(netWalletPourIn)}
            signedAmount={netWalletPourIn}
            hint="recharges − withdrawals · how much player money is currently pooled" />
        </div>
        <div className="text-[10.5px] text-arena-500 dark:text-neon-200/45 px-1 italic leading-relaxed">
          These figures are balance movements, not admin earnings. Admin holds player money in custody — it still belongs to the players until they withdraw it.
        </div>
      </ModalSection>

      {/* ── Roster ── */}
      <ModalSection title="Roster">
        <ModalStatsGrid stats={[
          { label: 'Total players', value: (data?.players.total ?? 0).toLocaleString('en-IN') },
          { label: 'Active in range', value: (data?.players.active_today ?? 0).toLocaleString('en-IN') },
          { label: 'New in range', value: (data?.players.new_week ?? 0).toLocaleString('en-IN') },
          { label: 'Suspended', value: (data?.players.suspended ?? 0).toLocaleString('en-IN'),
            tone: (data?.players.suspended ?? 0) > 0 ? 'warn' : undefined },
        ]} />
      </ModalSection>

      {/* ── Engagement (cross-cuts) ── */}
      <ModalSection title="Engagement">
        <div className="rounded-xl ring-1 ring-inset ring-arena-200/40 dark:ring-arena-600/30 bg-arena-50/40 dark:bg-arena-700/20 px-3 py-1 divide-y divide-arena-200/40 dark:divide-arena-600/30">
          <CrossCutRow icon="share" label="Players via referral"
            value={(data?.players.via_referral ?? 0).toLocaleString('en-IN')}
            hint="lifetime — players whose presence is credited to a referral bonus" />
          <CrossCutRow icon="redeem" label="Players who claimed an offer"
            value={`${(data?.players.with_offer_claims ?? 0).toLocaleString('en-IN')}${engagementPct > 0 ? ` (${engagementPct}% of active)` : ''}`}
            hint="distinct claimants in range" />
          <CrossCutRow icon="sports_esports" label="Total matches in range"
            value={(data?.today_glance.matches ?? 0).toLocaleString('en-IN')} />
        </div>
      </ModalSection>

      {/* ── Pending requests ── */}
      {(data && (data.money.pending_recharges > 0 || data.money.pending_withdrawals > 0)) && (
        <ModalSection title="Pending requests">
          <div className="flex flex-wrap gap-1.5">
            <ModalPendingChip label="Recharges"   count={data.money.pending_recharges}   to="/people/players/requests" onClose={onClose} />
            <ModalPendingChip label="Withdrawals" count={data.money.pending_withdrawals} to="/people/players/requests" onClose={onClose} />
          </div>
        </ModalSection>
      )}

      {/* ── Top players ── */}
      <ModalSection title="Top players by activity">
        <ModalTopList
          rows={(data?.players.top_active ?? []).slice(0, 5).map((p, i) => ({
            id: p.id, avatar_url: p.avatar_url, username: p.username,
            metric: `${p.matches.toLocaleString('en-IN')} matches`, rank: i + 1, to: `/people/players/${p.id}`,
          }))}
          empty="No matches in this range."
          onClose={onClose}
        />
      </ModalSection>
    </EntityModal>
  );
}

// ─── Agents modal — Profit-Stream redesign (cost-line framing) ────

function AgentsSummaryModal({ data, insights, onClose }: {
  data: AdminSummary | null;
  insights: AdminInsights | null;
  onClose: () => void;
}) {
  const commissionCost = Math.abs(data?.agents.commissions_in_range_amount ?? 0);
  const platformFeeShare = insights?.platformPnl
    ? null  // we don't have pillar-split commissions in the summary fetch
    : null;
  void platformFeeShare;

  // Top agents by True Net comes from the Phase-11F leaderboard fetch
  // already happening for the InsightRibbon — zero extra round-trip.
  const topByNet = insights?.leaderboard ?? [];

  return (
    <EntityModal
      tone="agent"
      icon="support_agent"
      title="Agents"
      subtitle="Commission cost · network · downline"
      primaryAction={{ label: 'Open Agents list', to: '/people/agents/list', icon: 'support_agent' }}
      onClose={onClose}
    >
      {/* ── Commission cost (the big rose number).
            When commissionCost === 0 we pass 0 so the headline reads
            '₹0' in neutral arena tone (no fake negative on a zero). */}
      <ProfitHeadline value={commissionCost > 0 ? -commissionCost : 0} label="Commissions paid to agents (range)" tone="agent" />

      <ModalSection title="What this cost bought">
        <CrossCutRow icon="payments" label="Agent topups (admin → agent pools)"
          value={inr(data?.agents.topups_in_range_amount ?? 0)}
          hint={data ? `${data.agents.topups_in_range_count} transactions` : undefined} />
        <CrossCutRow icon="trending_down" label="Agent outflow (pool → out)"
          value={inr(data?.agents.outflow_in_range_amount ?? 0)}
          hint={data ? `${data.agents.outflow_in_range_count} transactions` : undefined} />
        <CrossCutRow icon="account_balance" label="Total locked in agent pools"
          value={inr(data?.agents.total_pool ?? 0)} hint="all wallets, all-time balance" />
      </ModalSection>

      {/* ── Network breakdown (by level) ── */}
      <ModalSection title="Network shape">
        <ModalStatsGrid stats={[
          { label: 'Total agents', value: (data?.agents.total ?? 0).toLocaleString('en-IN') },
          { label: 'Active', value: (data?.agents.active ?? 0).toLocaleString('en-IN') },
          { label: 'Suspended', value: (data?.agents.suspended ?? 0).toLocaleString('en-IN'),
            tone: (data?.agents.suspended ?? 0) > 0 ? 'warn' : undefined },
        ]} />

        {(data?.agents.by_level?.length ?? 0) > 0 && (
          <div className="rounded-xl ring-1 ring-inset ring-arena-200/40 dark:ring-arena-600/30 bg-arena-50/40 dark:bg-arena-700/20 px-3 py-2.5">
            <div className="text-[10px] uppercase tracking-wider font-bold text-arena-500 dark:text-neon-200/55 mb-2">
              By chain level
            </div>
            <div className="flex flex-wrap gap-1.5">
              {data!.agents.by_level.map((lvl) => (
                <span key={lvl.level}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md
                                 bg-emerald-50 text-emerald-800 dark:bg-emerald-400/15 dark:text-emerald-200
                                 ring-1 ring-inset ring-emerald-200/60 dark:ring-emerald-400/25
                                 text-[10.5px] font-bold tabular-nums">
                  {levelName(lvl.level)} · {lvl.count}
                </span>
              ))}
            </div>
          </div>
        )}
      </ModalSection>

      {/* ── Cross-cuts (engagement) ── */}
      <ModalSection title="Engagement & opt-ins">
        <div className="rounded-xl ring-1 ring-inset ring-arena-200/40 dark:ring-arena-600/30 bg-arena-50/40 dark:bg-arena-700/20 px-3 py-1 divide-y divide-arena-200/40 dark:divide-arena-600/30">
          <CrossCutRow icon="share" label="Players via referral"
            value={(data?.players.via_referral ?? 0).toLocaleString('en-IN')}
            hint="lifetime — agent-network acquisition" />
          <CrossCutRow icon="how_to_reg" label="Pending offer opt-ins"
            value={(data?.rewards.pending_optins ?? 0).toLocaleString('en-IN')}
            hint="(offer × active agent) pairs awaiting decision" />
        </div>
      </ModalSection>

      {/* ── Pending agent requests ── */}
      {(data && data.money.pending_agent_withdrawals > 0) && (
        <ModalSection title="Pending requests">
          <div className="flex flex-wrap gap-1.5">
            <ModalPendingChip label="Agent withdrawals" count={data.money.pending_agent_withdrawals} to="/people/agents/requests" onClose={onClose} />
          </div>
        </ModalSection>
      )}

      {/* ── Top agents (by True Net P&L from Phase-11F leaderboard) ── */}
      {topByNet.length > 0 && (
        <ModalSection title="Top agents by True Net (7d)">
          <ul className="space-y-1">
            {topByNet.slice(0, 5).map((r, i) => {
              const net = Number(r.true_net);
              const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`;
              return (
                <li key={r.agent_id}>
                  <Link to={`/people/agents/${r.agent_id}`} onClick={onClose}
                        className="flex items-center gap-2 px-2 py-1.5 -mx-2 rounded-lg hover:bg-arena-50 dark:hover:bg-arena-700/60 transition">
                    <span className="grid place-items-center w-7 h-7 rounded-full bg-arena-100/60 dark:bg-arena-700/50 text-[11px] font-black tabular-nums">{medal}</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[12.5px] font-semibold text-neutral-900 dark:text-white truncate">@{r.agent_username}</div>
                      <div className="text-[10.5px] text-arena-600 dark:text-neon-200/50">{levelName(r.agent_level)} · {r.matches} match{r.matches === 1 ? '' : 'es'}</div>
                    </div>
                    <div className={`text-[12.5px] font-bold tabular-nums shrink-0 ${signColor(net)}`}>
                      {signedInr(net)}
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        </ModalSection>
      )}

      {/* ── Top agents (by owned players) — kept as secondary lens ── */}
      <ModalSection title="Top agents by owned players">
        <ModalTopList
          rows={(data?.agents.top_owners ?? []).slice(0, 5).map((a, i) => ({
            id: a.id, avatar_url: a.avatar_url, username: a.username,
            metric: `${a.owned_count} player${a.owned_count === 1 ? '' : 's'}`, rank: i + 1, to: `/people/agents/${a.id}`,
          }))}
          empty="No active agents yet."
          onClose={onClose}
        />
      </ModalSection>
    </EntityModal>
  );
}

// ─── Bots modal — Profit-Stream redesign ───────────────────────────

function BotsSummaryModal({ data, insights, onClose }: {
  data: AdminSummary | null;
  insights: AdminInsights | null;
  onClose: () => void;
}) {
  const matrix = insights?.botMatrix;
  const platformProfit = matrix?.platform_profit_window ?? 0;
  const walletNet = data?.bots.wallet_net_in_range_amount ?? 0;

  return (
    <EntityModal
      tone="bot"
      icon="smart_toy"
      title="Bots"
      subtitle="Platform profit · per-category · kill switches"
      onClose={onClose}
    >
      {/* ── Headline profit ── */}
      <ProfitHeadline value={platformProfit} label="Platform profit from bot ecosystem (7d)" tone="bot" />

      {/* ── Per-category P&L ──
          2026-05-16 (color audit): container is NEUTRAL so the
          sign-driven P&L color is unambiguous. The category (easy/
          medium/hard) is shown as a small tinted chip at the top —
          difficulty identity stays visible without competing with
          the green/rose profit signal. */}
      {matrix && matrix.categories.length > 0 && (
        <ModalSection title="Per-category P&L (7d)">
          <div className="grid grid-cols-3 gap-2">
            {matrix.categories.map((c) => {
              // platformProfit = -bot_net_p_l per PlatformPanel
              // convention. Positive = platform earned from this
              // category; negative = platform paid out.
              const platformProfit = -c.bot_net_p_l;
              const numberColor = signColor(platformProfit);
              const chipTone = c.category === 'easy'
                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-400/20 dark:text-emerald-300'
                : c.category === 'medium'
                ? 'bg-amber-100 text-amber-700 dark:bg-amber-400/20 dark:text-amber-300'
                : 'bg-rose-100 text-rose-700 dark:bg-rose-400/20 dark:text-rose-300';
              return (
                <div key={c.category}
                     className={`rounded-xl px-2.5 py-2.5 ring-1 ring-inset
                                 bg-white dark:bg-arena-700/40
                                 ring-arena-200/50 dark:ring-arena-600/40
                                 ${!c.kill_switch_enabled ? 'opacity-50' : ''}`}>
                  {/* Top row — category chip + (optional) kill-switch icon */}
                  <div className="flex items-center justify-between gap-1 mb-1">
                    <span className={`px-1.5 py-0.5 rounded-md text-[9.5px] font-bold uppercase tracking-wider ${chipTone}`}>
                      {c.category}
                    </span>
                    {!c.kill_switch_enabled && (
                      <Icon name="block" size={11} className="text-arena-500 dark:text-neon-200/45" />
                    )}
                  </div>

                  {/* Sign-driven big P&L number */}
                  <div className={`text-[14px] font-bold tabular-nums leading-tight ${numberColor}`}>
                    {signedInr(platformProfit)}
                  </div>

                  {/* Neutral match count */}
                  <div className="text-[10px] text-arena-600 dark:text-neon-200/55 mt-0.5">
                    {c.match_count.toLocaleString('en-IN')} matches
                  </div>
                </div>
              );
            })}
          </div>
          {matrix.categories.some((c) => c.bot_net_p_l > 0 && c.kill_switch_enabled) && (
            <div className="text-[10.5px] text-rose-700 dark:text-rose-300 px-1 flex items-start gap-1.5">
              <Icon name="warning" size={11} filled className="mt-0.5 shrink-0" />
              <span>One or more categories are draining money. Consider toggling their kill switch — drill into Bot performance below.</span>
            </div>
          )}
        </ModalSection>
      )}

      {/* ── Activity ── */}
      <ModalSection title="Roster & activity">
        <ModalStatsGrid stats={[
          { label: 'Total bots', value: (data?.bots.total ?? 0).toLocaleString('en-IN') },
          { label: 'Active', value: (data?.bots.active ?? 0).toLocaleString('en-IN') },
          { label: 'Matches in range', value: matrix
            ? matrix.categories.reduce((s, c) => s + c.match_count, 0).toLocaleString('en-IN')
            : '—' },
        ]} />
      </ModalSection>

      {/* ── Wallet net ──
          Total bot wallet = neutral balance.
          Net movement = signed P&L event → color-driven by sign.
          Note: bot wallet GROWING (signedAmount > 0) is platform-
          favorable (players paid into bot pool), so emerald. Wallet
          shrinking = platform paid out → rose. */}
      <ModalSection title="Bot wallet">
        <CrossCutRow icon="account_balance_wallet" label="Total in bot wallets"
          value={inr(data?.bots.total_wallet ?? 0)} hint="all bots, current balance" />
        <CrossCutRow icon={walletNet >= 0 ? 'trending_up' : 'trending_down'} label="Net wallet movement (range)"
          value={signedInr(walletNet)}
          signedAmount={walletNet}
          hint={walletNet > 0
            ? 'bots gained money — players paid in'
            : walletNet < 0
              ? 'bots drained — payouts to players'
              : 'no net movement'} />
      </ModalSection>

      {/* ── Two CTAs at the bottom (drill + roster) ── */}
      <ModalSection title="Drill deeper">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Link to="/?drill=bots" onClick={onClose}
                className="rounded-xl px-3 py-2.5
                           bg-arena-50/60 hover:bg-arena-100/60 dark:bg-arena-700/30 dark:hover:bg-arena-700/50
                           ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 transition">
            <div className="flex items-center gap-2">
              <Icon name="precision_manufacturing" size={14} className="text-amber-700 dark:text-amber-300" />
              <div className="min-w-0 flex-1">
                <div className="text-[12px] font-semibold text-neutral-900 dark:text-white">Bot performance</div>
                <div className="text-[10px] text-arena-600 dark:text-neon-200/60">Kill switches · per-game P&L</div>
              </div>
              <Icon name="arrow_forward" size={13} className="text-arena-400 dark:text-neon-200/45 shrink-0" />
            </div>
          </Link>
          <Link to="/people/bots" onClick={onClose}
                className="rounded-xl px-3 py-2.5
                           bg-arena-50/60 hover:bg-arena-100/60 dark:bg-arena-700/30 dark:hover:bg-arena-700/50
                           ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 transition">
            <div className="flex items-center gap-2">
              <Icon name="smart_toy" size={14} className="text-amber-700 dark:text-amber-300" />
              <div className="min-w-0 flex-1">
                <div className="text-[12px] font-semibold text-neutral-900 dark:text-white">Bot roster</div>
                <div className="text-[10px] text-arena-600 dark:text-neon-200/60">CRUD individual bots</div>
              </div>
              <Icon name="arrow_forward" size={13} className="text-arena-400 dark:text-neon-200/45 shrink-0" />
            </div>
          </Link>
        </div>
      </ModalSection>
    </EntityModal>
  );
}

// ─── My Wallet (admin) modal — LIVE (Phase 7.7, 2026-05-17) ─────────

/**
 * MyWalletModal — admin's operational P&L + deposit liability.
 *
 * Sourced from the double-entry ledgers wired in Phase 7.3/7.4
 * (admin_wallet / admin_wallet_ledger / admin_deposit_wallet /
 * admin_deposit_ledger). The headline is admin's LIFETIME P&L
 * (signed, can go arbitrarily negative per Override 1). The deposit
 * section is L1-only liability (Override 2) — always ≥ 0, rendered
 * NEUTRAL (it's money admin holds, not profit).
 *
 * Color/sign semantics enforced:
 *   - Profit (P&L > 0)  → green via signColor()
 *   - Loss   (P&L < 0)  → red   via signColor()
 *   - Zero / deposit / liability totals → neutral arena tone
 */
function MyWalletModal({ data, range, onClose }: {
  data: AdminSummary | null;
  range: Range;
  onClose: () => void;
}) {
  const aw = data?.admin_wallet ?? null;
  const ad = data?.admin_deposit ?? null;
  const rangeLbl = rangeLabel(range);

  return (
    <EntityModal
      tone="wallet"
      icon="account_balance_wallet"
      title="My Wallet"
      subtitle="Admin operational wallet · Super Master deposit liability"
      onClose={onClose}
    >
      {/* Headline — LIFETIME P&L. Signed; green/red/neutral by sign. */}
      {aw !== null && (
        <ProfitHeadline value={aw.lifetime_balance} label="Lifetime P&L" tone="wallet" />
      )}

      {/* ── Admin Operational Wallet (the P&L wallet) ── */}
      <ModalSection title="Admin Wallet — operational P&L">
        <div className="rounded-xl px-3 py-3
                        bg-arena-50/60 dark:bg-arena-700/30
                        ring-1 ring-inset ring-arena-200/40 dark:ring-arena-600/30 text-center">
          <div className={`text-2xl sm:text-3xl font-display font-extrabold tabular-nums leading-none tracking-tight ${aw ? signColor(aw.lifetime_balance) : 'text-arena-700 dark:text-neon-200/80'}`}>
            {aw ? signedInr(aw.lifetime_balance) : '₹—'}
          </div>
          <div className="text-[11px] text-arena-600 dark:text-neon-200/60 mt-1.5">
            Current balance — lifetime, signed
          </div>
        </div>
        <ModalStatsGrid stats={[
          // In-range net is signed → render with signed/color.
          { label: `${rangeLbl} net P&L`,
            value: aw ? signedInr(aw.in_range_net) : '₹—',
            tone: aw ? (aw.in_range_net > 0 ? 'positive' : aw.in_range_net < 0 ? 'negative' : 'neutral') : 'neutral' },
          // In-range inflow is always ≥ 0; neutral display (it's a
          // positive component, but pairing it with a green +sign
          // would imply admin "earned" recharges — which they didn't,
          // recharges aren't in admin_wallet_ledger anyway).
          { label: `${rangeLbl} inflow`,
            value: aw ? inr(aw.in_range_inflow) : '₹—',
            tone: 'neutral' },
          // Outflow comes back signed (≤ 0). Render with absolute
          // value + leading − so it reads naturally as a "spent" line.
          { label: `${rangeLbl} outflow`,
            value: aw ? `−${inr(Math.abs(aw.in_range_outflow))}` : '₹—',
            tone: 'neutral' },
          { label: `${rangeLbl} entries`,
            value: aw ? aw.in_range_entries.toLocaleString('en-IN') : '—',
            tone: 'neutral' },
        ]} />
      </ModalSection>

      {/* ── Admin Deposit Wallet — L1 liability tracker ── */}
      <ModalSection title="Admin Deposit Wallet — L1 liability">
        <div className="rounded-xl px-3 py-3
                        bg-arena-50/60 dark:bg-arena-700/30
                        ring-1 ring-inset ring-arena-200/40 dark:ring-arena-600/30 text-center">
          {/* Liability is always ≥ 0 — neutral colour, NOT profit-green. */}
          <div className="text-2xl sm:text-3xl font-display font-extrabold tabular-nums leading-none tracking-tight text-arena-700 dark:text-neon-200/80">
            {ad ? inr(ad.liability) : '₹—'}
          </div>
          <div className="text-[11px] text-arena-600 dark:text-neon-200/60 mt-1.5">
            Currently held on behalf of L1 agents · must be returned on exit
          </div>
        </div>
        <ModalStatsGrid stats={[
          { label: `${rangeLbl} received`,
            value: ad ? inr(ad.in_range_received) : '₹—',
            tone: 'neutral' },
          { label: `${rangeLbl} consumed`,
            value: ad ? inr(ad.in_range_consumed) : '₹—',
            tone: 'neutral' },
          { label: `${rangeLbl} refunded`,
            value: ad ? inr(ad.in_range_refunded) : '₹—',
            tone: 'neutral' },
          { label: `${rangeLbl} entries`,
            value: ad ? ad.in_range_entries.toLocaleString('en-IN') : '—',
            tone: 'neutral' },
        ]} />
        <div className="text-[10.5px] text-arena-500 dark:text-neon-200/45 leading-relaxed italic px-1">
          L1-only: only deposits from agents directly under admin are tracked here. Sub-agent deposits sit on their
          respective parents' books per the 5-level chain rule.
        </div>
      </ModalSection>
    </EntityModal>
  );
}

// ════════════════════════════════════════════════════════════════════
// PATH C MERGE (2026-05-16) — InsightRibbon (Zone 2) + DrillPanel (Zone 3)
//
// The 6 ribbon cards each show a compact at-a-glance metric. Tapping
// any card pushes `?drill=<key>` onto the URL — DrillPanel observes
// the param and mounts the corresponding full Operations panel inline.
// Only one drill is active at a time, so mobile users never face a
// stacked-panels scroll bomb.
// ════════════════════════════════════════════════════════════════════

type RibbonTone = 'violet' | 'amber' | 'cyan' | 'rose' | 'indigo' | 'emerald';

const RIBBON_TONES: Record<RibbonTone, {
  iconBg: string; iconText: string; ring: string; accent: string;
}> = {
  violet:  { iconBg: 'bg-violet-50 dark:bg-violet-400/15',   iconText: 'text-violet-700 dark:text-violet-300',   ring: 'ring-violet-200 dark:ring-violet-400/30',   accent: 'text-violet-700 dark:text-violet-300' },
  amber:   { iconBg: 'bg-amber-50 dark:bg-amber-400/15',     iconText: 'text-amber-700 dark:text-amber-300',     ring: 'ring-amber-200 dark:ring-amber-400/30',     accent: 'text-amber-700 dark:text-amber-300' },
  cyan:    { iconBg: 'bg-cyan-50 dark:bg-cyan-400/15',       iconText: 'text-cyan-700 dark:text-cyan-300',       ring: 'ring-cyan-200 dark:ring-cyan-400/30',       accent: 'text-cyan-700 dark:text-cyan-300' },
  rose:    { iconBg: 'bg-rose-50 dark:bg-rose-400/15',       iconText: 'text-rose-700 dark:text-rose-300',       ring: 'ring-rose-200 dark:ring-rose-400/30',       accent: 'text-rose-700 dark:text-rose-300' },
  indigo:  { iconBg: 'bg-indigo-50 dark:bg-indigo-400/15',   iconText: 'text-indigo-700 dark:text-indigo-300',   ring: 'ring-indigo-200 dark:ring-indigo-400/30',   accent: 'text-indigo-700 dark:text-indigo-300' },
  emerald: { iconBg: 'bg-emerald-50 dark:bg-emerald-400/15', iconText: 'text-emerald-700 dark:text-emerald-300', ring: 'ring-emerald-200 dark:ring-emerald-400/30', accent: 'text-emerald-700 dark:text-emerald-300' },
};

/**
 * InsightCard — the primitive shape for every card on the
 * InsightRibbon. Headline is the big metric, sub is a smaller
 * supporting line, body is for chips/sparkline-style content.
 *
 * Whole card is a button — entire surface is the tap target.
 * Active state (`isActive`) means the card's drill is currently
 * open below; we add a subtle ring + chevron rotation so the user
 * can see which card they came from while scrolling the panel.
 */
function InsightCard({
  icon, title, tone, isActive, onOpen, children,
}: {
  icon: string;
  title: string;
  tone: RibbonTone;
  isActive: boolean;
  onOpen: () => void;
  children: ReactNode;
}) {
  const t = RIBBON_TONES[tone];
  return (
    <motion.button
      onClick={onOpen}
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -1 }}
      transition={{ type: 'spring', stiffness: 380, damping: 26 }}
      className={`
        text-left rounded-2xl p-3 flex flex-col gap-2
        bg-white dark:bg-arena-700/40
        ring-1 ring-inset
        ${isActive ? `ring-2 ${t.ring}` : 'ring-arena-200/50 dark:ring-arena-600/40'}
        shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
        hover:ring-2 hover:${t.ring}
        focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/50
        transition
      `}
    >
      <div className="flex items-center justify-between gap-2 min-w-0">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className={`grid place-items-center w-7 h-7 rounded-lg ${t.iconBg} ${t.iconText} shrink-0`}>
            <Icon name={icon} size={14} filled />
          </span>
          <span className="text-[11px] font-semibold text-arena-700 dark:text-neon-200/80 truncate">{title}</span>
        </div>
        <Icon
          name={isActive ? 'expand_less' : 'expand_more'}
          size={14}
          className={`shrink-0 transition ${isActive ? t.accent : 'text-arena-400 dark:text-neon-200/35'}`}
        />
      </div>
      {children}
    </motion.button>
  );
}

/** PlatformMini — Platform True Net (7d) + worker health badge. */
function PlatformRibbonCard({ data, isActive, onOpen }: {
  data: AdminInsights | null; isActive: boolean; onOpen: () => void;
}) {
  const net = data?.platformPnl?.platformTrueNet ?? 0;
  const wh  = data?.workerHealth;
  const atRisk = data?.depositsAtRisk ?? 0;
  return (
    <InsightCard icon="monitoring" title="Platform P&L" tone="violet" isActive={isActive} onOpen={onOpen}>
      {data === null
        ? <div className="text-[11px] text-arena-500 dark:text-neon-200/45">Loading…</div>
        : <>
          <div className={`text-[18px] sm:text-[20px] font-display font-extrabold tabular-nums leading-none tracking-tight ${signColor(net)}`}>
            {signedInr(net)}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            {wh && (
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                wh.healthy
                  ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300'
                  : 'bg-amber-50 text-amber-700 dark:bg-amber-400/15 dark:text-amber-300'}`}>
                <Icon name={wh.healthy ? 'check_circle' : 'warning'} size={10} filled /> {wh.healthy ? 'Healthy' : 'Degraded'}
              </span>
            )}
            {atRisk > 0 && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-rose-50 text-rose-700 dark:bg-rose-400/15 dark:text-rose-300">
                <Icon name="warning" size={10} /> {atRisk} at risk
              </span>
            )}
          </div>
        </>}
    </InsightCard>
  );
}

/** Leaderboard ribbon — top earner's net + agent count. */
function LeaderboardRibbonCard({ data, isActive, onOpen }: {
  data: AdminInsights | null; isActive: boolean; onOpen: () => void;
}) {
  const top = data?.leaderboard?.[0];
  const count = data?.leaderboard?.length ?? 0;
  return (
    <InsightCard icon="leaderboard" title="Top earners (7d)" tone="amber" isActive={isActive} onOpen={onOpen}>
      {data === null
        ? <div className="text-[11px] text-arena-500 dark:text-neon-200/45">Loading…</div>
        : top
          ? <>
            <div className="text-[13px] font-bold text-neutral-900 dark:text-white truncate">@{top.agent_username}</div>
            <div className={`text-[15px] font-display font-extrabold tabular-nums leading-none ${signColor(Number(top.true_net))}`}>
              {signedInr(Number(top.true_net))}
            </div>
            <div className="text-[10px] text-arena-600 dark:text-neon-200/55 truncate">{levelName(top.agent_level)} · {count} ranked</div>
          </>
          : <div className="text-[11px] text-arena-500 dark:text-neon-200/45">No earnings (7d)</div>}
    </InsightCard>
  );
}

/** Activity ribbon — event count + breakdown by kind (24h). */
function ActivityRibbonCard({ data, isActive, onOpen }: {
  data: AdminInsights | null; isActive: boolean; onOpen: () => void;
}) {
  const items = data?.activity ?? [];
  const matches = items.filter(i => i.kind === 'match').length;
  const recharges = items.filter(i => i.kind === 'recharge').length;
  const rates = items.filter(i => i.kind === 'partnership_change').length;
  return (
    <InsightCard icon="dynamic_feed" title="Activity (24h)" tone="cyan" isActive={isActive} onOpen={onOpen}>
      {data === null
        ? <div className="text-[11px] text-arena-500 dark:text-neon-200/45">Loading…</div>
        : <>
          <div className="text-[18px] sm:text-[20px] font-display font-extrabold tabular-nums leading-none text-cyan-700 dark:text-cyan-300">
            {items.length}
          </div>
          <div className="text-[10px] text-arena-600 dark:text-neon-200/55 flex items-center gap-1.5 flex-wrap">
            {matches > 0  && <span>🎮 {matches}</span>}
            {recharges > 0 && <span>💸 {recharges}</span>}
            {rates > 0    && <span>⚙ {rates}</span>}
            {items.length === 0 && <span>Quiet — no events</span>}
          </div>
        </>}
    </InsightCard>
  );
}

/** Bot perf ribbon — platform profit + 3 category chips. */
function BotsRibbonCard({ data, isActive, onOpen }: {
  data: AdminInsights | null; isActive: boolean; onOpen: () => void;
}) {
  const m = data?.botMatrix ?? null;
  return (
    <InsightCard icon="precision_manufacturing" title="Bot perf (7d)" tone="rose" isActive={isActive} onOpen={onOpen}>
      {data === null
        ? <div className="text-[11px] text-arena-500 dark:text-neon-200/45">Loading…</div>
        : m
          ? <>
            <div className={`text-[18px] sm:text-[20px] font-display font-extrabold tabular-nums leading-none ${signColor(m.platform_profit_window)}`}>
              {signedInr(m.platform_profit_window)}
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              {m.categories.map((c) => {
                const drain = c.bot_net_p_l > 0;
                return (
                  <span key={c.category}
                    className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9.5px] font-bold uppercase tracking-wider
                      ${c.category === 'easy' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300'
                        : c.category === 'medium' ? 'bg-amber-50 text-amber-700 dark:bg-amber-400/15 dark:text-amber-300'
                        : 'bg-rose-50 text-rose-700 dark:bg-rose-400/15 dark:text-rose-300'}
                      ${!c.kill_switch_enabled ? 'opacity-40' : ''}`}>
                    {c.category[0]}{drain ? '↓' : '↑'}
                  </span>
                );
              })}
            </div>
            <div className="text-[10px] text-arena-600 dark:text-neon-200/55">{m.total_active} of {m.total_bots} active</div>
          </>
          : <div className="text-[11px] text-arena-500 dark:text-neon-200/45">Matrix not loaded</div>}
    </InsightCard>
  );
}

/** Audit ribbon — latest rate-change preview + count. */
function AuditRibbonCard({ data, isActive, onOpen }: {
  data: AdminInsights | null; isActive: boolean; onOpen: () => void;
}) {
  const rows = data?.auditRows ?? [];
  const latest = rows[0];
  return (
    <InsightCard icon="history" title="Rate changes" tone="indigo" isActive={isActive} onOpen={onOpen}>
      {data === null
        ? <div className="text-[11px] text-arena-500 dark:text-neon-200/45">Loading…</div>
        : latest
          ? <>
            <div className="text-[13px] font-bold text-neutral-900 dark:text-white truncate">@{latest.agent_username || `#${latest.agent_id}`}</div>
            <div className="text-[12px] font-mono tabular-nums text-arena-700 dark:text-neon-200/80">
              {latest.old_rate_pct == null ? '—' : `${Number(latest.old_rate_pct)}%`} → <b>{Number(latest.new_rate_pct)}%</b>
            </div>
            <div className="text-[10px] text-arena-600 dark:text-neon-200/55 truncate">
              {rows.length} recent · {latest.pillar}
            </div>
          </>
          : <div className="text-[11px] text-arena-500 dark:text-neon-200/45">No changes yet</div>}
    </InsightCard>
  );
}

/** Health / DLQ ribbon — DLQ depth + worker queue. */
function HealthRibbonCard({ data, isActive, onOpen }: {
  data: AdminInsights | null; isActive: boolean; onOpen: () => void;
}) {
  const dlqCount = data?.dlqRows?.length ?? 0;
  const wh = data?.workerHealth;
  const ok = dlqCount === 0;
  return (
    <InsightCard icon={ok ? 'check_circle' : 'warning'} title="Settlement health" tone={ok ? 'emerald' : 'rose'} isActive={isActive} onOpen={onOpen}>
      {data === null
        ? <div className="text-[11px] text-arena-500 dark:text-neon-200/45">Loading…</div>
        : <>
          <div className={`text-[18px] sm:text-[20px] font-display font-extrabold leading-none ${ok ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}>
            {ok ? '✓ Clean' : `${dlqCount} DLQ`}
          </div>
          <div className="text-[10px] text-arena-600 dark:text-neon-200/55">
            {wh ? <>Queue {wh.queueDepth} · DLQ {wh.dlqDepth}</> : 'Settlement worker'}
          </div>
        </>}
    </InsightCard>
  );
}

/**
 * TestingPhaseBar (owner, 2026-09-20) — "no profit getting calculated in the admin".
 * The platform rule leaves test accounts out of every book, so while the product is
 * tested from test_user / test_agent every profit view showed zero. This switch is a
 * server-side default (platform_config `reports_include_test`) read by the DISPLAY
 * endpoints — Platform P&L, dashboards, leaderboards, the Game money report, the Matka
 * dashboard/week views. The money core (rollups, reserve, chain payouts) never reads it.
 */
function TestingPhaseBar({ onChanged }: { onChanged: () => void }) {
  const [on, setOn] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    adminApi.get<{ includeTest: boolean }>('/admin/reports/test-mode')
      .then((d) => setOn(!!d.includeTest)).catch(() => setOn(false));
  }, []);
  const toggle = async () => {
    if (on === null || busy) return;
    setBusy(true);
    try {
      const d = await adminApi.put<{ includeTest: boolean }>('/admin/reports/test-mode', { includeTest: !on });
      setOn(!!d.includeTest); onChanged();
    } catch { /* keep the shown state */ } finally { setBusy(false); }
  };
  return (
    <div className={`mx-2 lg:mx-4 mb-2 flex items-center justify-between gap-3 rounded-xl border px-3 py-2 ${
      on ? 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-100'
         : 'border-arena-200 bg-white text-arena-700 dark:border-neon-200/10 dark:bg-white/5 dark:text-neon-200/70'}`}>
      <div className="min-w-0">
        <div className="text-[12px] font-bold flex items-center gap-1.5">
          <Icon name={on ? 'science' : 'verified'} size={14} filled />
          {on === null ? 'Checking…' : on ? 'Testing phase — counting test accounts' : 'Live books — test accounts hidden'}
        </div>
        <div className="text-[11px] opacity-80">
          {on ? 'Every dashboard and report below includes play from test_user / test_agent. Switch this off before launch.'
              : 'Play from test accounts is left out of every profit figure (platform rule). Turn this on while you test.'}
        </div>
      </div>
      <button type="button" role="switch" aria-checked={!!on} onClick={toggle} disabled={on === null || busy}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition disabled:opacity-50 ${on ? 'bg-amber-500' : 'bg-arena-300 dark:bg-white/20'}`}>
        <span className="sr-only">Count test accounts in dashboards and reports</span>
        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${on ? 'translate-x-5' : 'translate-x-0.5'}`} />
      </button>
    </div>
  );
}

/**
 * InsightRibbon — Zone 2 of the dashboard. Six cards (Platform first
 * per the architectural decision) in a responsive grid: 2-col mobile,
 * 3-col tablet, 6-col desktop.
 */
function InsightRibbon({
  data, drill, onOpen,
}: {
  data: AdminInsights | null;
  drill: DrillKey | null;
  onOpen: (k: DrillKey) => void;
}) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3 px-2 lg:px-4">
      <PlatformRibbonCard    data={data} isActive={drill === 'platform'}     onOpen={() => onOpen('platform')} />
      <LeaderboardRibbonCard data={data} isActive={drill === 'leaderboards'} onOpen={() => onOpen('leaderboards')} />
      <ActivityRibbonCard    data={data} isActive={drill === 'activity'}     onOpen={() => onOpen('activity')} />
      <BotsRibbonCard        data={data} isActive={drill === 'bots'}         onOpen={() => onOpen('bots')} />
      <AuditRibbonCard       data={data} isActive={drill === 'audit'}        onOpen={() => onOpen('audit')} />
      <HealthRibbonCard      data={data} isActive={drill === 'dlq'}          onOpen={() => onOpen('dlq')} />
    </div>
  );
}

/**
 * DrillPanel — Zone 3 of the dashboard. Mounts ONE of the six full
 * Operations panels based on the URL `?drill=…` state. The panel
 * file is imported as-is — same filters, same modals, same SSE.
 *
 * The header has the panel title + a Close button that strips the
 * drill query param. Below the header sits the panel's existing JSX.
 *
 * AnimatePresence on the parent gives a soft fade+lift in/out so
 * users see the drill arrive instead of a hard pop.
 */
function DrillPanel({ drill, onClose }: { drill: DrillKey; onClose: () => void }) {
  const meta = DRILL_TITLES[drill];
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.22 }}
      className="px-2 lg:px-4"
    >
      {/* Header bar — sticky inside the drill so the Close button
          stays in reach when the panel itself scrolls long content. */}
      <div className="sticky top-0 z-20 -mx-2 px-2 lg:-mx-4 lg:px-4 py-2
                      backdrop-blur-md bg-white/85 dark:bg-arena-900/75
                      border-b border-arena-200/50 dark:border-arena-600/40
                      flex items-center gap-2">
        <Icon name={meta.icon} size={16} className="text-violet-700 dark:text-violet-300" />
        <h2 className="text-[14px] font-bold text-neutral-900 dark:text-white">{meta.title}</h2>
        <div className="flex-1" />
        <button onClick={onClose}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[11.5px] font-semibold
                           bg-white dark:bg-arena-700/50
                           ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/50
                           text-arena-700 dark:text-neon-200/80
                           hover:bg-arena-50 dark:hover:bg-arena-700/70 transition">
          <Icon name="close" size={13} /> Close
        </button>
      </div>

      <div className="mt-3">
        {drill === 'platform'     && <PlatformPanel />}
        {drill === 'leaderboards' && <LeaderboardsPanel scope="admin" />}
        {drill === 'activity'     && <ActivityFeedPanel scope="admin" />}
        {drill === 'bots'         && <BotMatrixPanel />}
        {drill === 'audit'        && <AuditLogPanel />}
        {drill === 'dlq'          && <DlqPanel />}
      </div>
    </motion.div>
  );
}

// ─── Phase 11F: insight mini-cards used inside the dashboard ─────

function LeaderboardMini({ rows }: { rows: LeaderboardRow[] }) {
  if (!rows.length) return <div className="text-center text-[12px] text-arena-500 dark:text-neon-200/45 py-5">No earnings in the last 7 days.</div>;
  return (
    <ul className="space-y-1">
      {rows.slice(0, 5).map((r, i) => {
        const net = Number(r.true_net);
        const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`;
        return (
          <li key={r.agent_id}>
            <Link to={`/people/agents/${r.agent_id}`} className="flex items-center gap-2 px-2 py-1.5 -mx-2 rounded-lg hover:bg-arena-50 dark:hover:bg-arena-700/60 transition">
              <span className="grid place-items-center w-7 h-7 rounded-full bg-arena-100/60 dark:bg-arena-700/50 text-[11px] font-black tabular-nums">{medal}</span>
              <div className="min-w-0 flex-1">
                <div className="text-[12.5px] font-semibold text-neutral-900 dark:text-white truncate">@{r.agent_username}</div>
                <div className="text-[10.5px] text-arena-600 dark:text-neon-200/50">{levelName(r.agent_level)} · {r.matches} match{r.matches === 1 ? '' : 'es'}</div>
              </div>
              <div className={`text-[12.5px] font-bold tabular-nums shrink-0 ${net >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}>
                {net >= 0 ? '+' : '−'}{inr(Math.abs(net))}
              </div>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

function ActivityMini({ items }: { items: FeedItem[] }) {
  if (!items.length) return <div className="text-center text-[12px] text-arena-500 dark:text-neon-200/45 py-5">Quiet — no events in the last 24h.</div>;
  return (
    <ul className="space-y-1">
      {items.slice(0, 5).map((it) => {
        const icon = it.kind === 'match' ? 'sports_esports' : it.kind === 'recharge' ? 'payments' : 'percent';
        const tone = it.kind === 'match' ? 'bg-cyan-50 text-cyan-600 dark:bg-cyan-400/15 dark:text-cyan-300'
                   : it.kind === 'recharge' ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-400/15 dark:text-emerald-300'
                   : 'bg-amber-50 text-amber-600 dark:bg-amber-400/15 dark:text-amber-300';
        return (
          <li key={`${it.kind}-${it.ref_id}`} className="px-2 py-1.5 -mx-2 rounded-lg flex items-start gap-2">
            <span className={`grid place-items-center w-6 h-6 rounded-full shrink-0 ${tone}`}>
              <Icon name={icon} size={13} />
            </span>
            <div className="min-w-0 flex-1 text-[12px]">
              <ActivityMiniLine it={it} />
              <div className="text-[10px] text-arena-400 dark:text-neon-200/35">{new Date(it.at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function ActivityMiniLine({ it }: { it: FeedItem }) {
  if (it.kind === 'match') {
    const amt = Number(it.amount || 0);
    return (
      <div className="text-arena-700 dark:text-neon-200/85 truncate">
        <b>@{it.agent_username}</b>{' '}
        <span className="text-arena-600 dark:text-neon-200/70">earned</span>{' '}
        <span className={`font-bold tabular-nums ${amt >= 0 ? 'text-emerald-600 dark:text-emerald-300' : 'text-rose-600 dark:text-rose-300'}`}>
          {amt >= 0 ? '+' : '−'}{inr(Math.abs(amt))}
        </span>
        {' '}<span className="text-arena-600 dark:text-neon-200/70">on {it.game_id}</span>
      </div>
    );
  }
  if (it.kind === 'recharge') {
    return (
      <div className="text-arena-700 dark:text-neon-200/85 truncate">
        <span className="text-arena-600 dark:text-neon-200/70">Recharge</span>{' '}
        <b className="capitalize">{it.state}</b>{' '}
        <span className="text-arena-600 dark:text-neon-200/70">— ₹{Number(it.amount).toLocaleString('en-IN')}</span>
        {it.agent_username && <> <span className="text-arena-600 dark:text-neon-200/70">via</span> <b>@{it.agent_username}</b></>}
      </div>
    );
  }
  return (
    <div className="text-arena-700 dark:text-neon-200/85 truncate">
      <span className="text-arena-600 dark:text-neon-200/70">Rate change</span>{' '}
      <b>@{it.agent_username}</b>{' '}
      <span className="font-mono">{it.old_rate_pct ?? '—'}% → {it.new_rate_pct}%</span>
      {it.is_cascade && <span className="ml-1 text-[9px] uppercase font-bold tracking-wider px-1 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-400/15 dark:text-amber-300">cascade</span>}
    </div>
  );
}

function BotMatrixMini({ matrix }: { matrix: AdminInsights['botMatrix'] }) {
  if (!matrix) return <div className="text-center text-[12px] text-arena-500 dark:text-neon-200/45 py-5">Loading…</div>;
  const profit = matrix.platform_profit_window;
  return (
    <div className="space-y-2.5">
      <div className="flex items-baseline justify-between">
        <span className="text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/70 font-bold">Platform profit (7d)</span>
        <span className={`text-lg font-display font-black tabular-nums ${profit >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}>
          {profit >= 0 ? '+' : '−'}{inr(Math.abs(profit))}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {matrix.categories.map((c) => {
          const draining = c.bot_net_p_l > 0;
          const tone = c.category === 'easy' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300'
                     : c.category === 'medium' ? 'bg-amber-50 text-amber-700 dark:bg-amber-400/15 dark:text-amber-300'
                     : 'bg-rose-50 text-rose-700 dark:bg-rose-400/15 dark:text-rose-300';
          return (
            <div key={c.category} className={`rounded-lg px-2 py-1.5 ${tone} ${!c.kill_switch_enabled ? 'opacity-50' : ''}`}>
              <div className="text-[9.5px] uppercase tracking-wider font-bold">{c.category}</div>
              <div className={`text-[12.5px] font-bold tabular-nums ${draining ? 'text-rose-700 dark:text-rose-300' : ''}`}>
                {c.bot_net_p_l >= 0 ? '+' : '−'}{inr(Math.abs(c.bot_net_p_l))}
              </div>
              <div className="text-[9.5px]">{c.match_count}m</div>
            </div>
          );
        })}
      </div>
      <div className="text-[10.5px] text-arena-600 dark:text-neon-200/55">{matrix.total_active} of {matrix.total_bots} bots active</div>
    </div>
  );
}

function AuditMini({ rows }: { rows: AdminInsights['auditRows'] }) {
  if (!rows.length) return <div className="text-center text-[12px] text-arena-500 dark:text-neon-200/45 py-5">No rate-change events yet.</div>;
  return (
    <ul className="space-y-1">
      {rows.slice(0, 5).map((r) => {
        const oldR = r.old_rate_pct == null ? null : Number(r.old_rate_pct);
        const newR = Number(r.new_rate_pct);
        return (
          <li key={r.id} className="px-2 py-1.5 -mx-2 rounded-lg flex items-center gap-2">
            <Icon name="history" size={14} className="text-violet-600 dark:text-violet-300 shrink-0" />
            <div className="min-w-0 flex-1 text-[12px]">
              <div className="truncate">
                <b>@{r.agent_username || `#${r.agent_id}`}</b>{' '}
                <span className="text-arena-600 dark:text-neon-200/70">{r.pillar} · {r.game_id}</span>
              </div>
              <div className="text-[10.5px] text-arena-600 dark:text-neon-200/50 font-mono">
                {oldR == null ? '—' : `${oldR}%`} → <b>{newR}%</b>
                {r.is_cascade ? <span className="ml-1 text-[9px] uppercase font-bold tracking-wider px-1 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-400/15 dark:text-amber-300">cascade</span> : null}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function OpsHealthMini({ insights }: { insights: AdminInsights | null }) {
  const dlqCount = insights?.dlqRows.length ?? 0;
  const dlqOk = dlqCount === 0;
  return (
    <div className="space-y-2.5">
      <div className={`rounded-xl px-3 py-2.5 flex items-center gap-3 ${
        dlqOk
          ? 'bg-emerald-50 dark:bg-emerald-400/[0.08] ring-1 ring-inset ring-emerald-200/60 dark:ring-emerald-400/30'
          : 'bg-rose-50 dark:bg-rose-400/[0.08] ring-1 ring-inset ring-rose-200/60 dark:ring-rose-400/30'
      }`}>
        <Icon name={dlqOk ? 'check_circle' : 'warning'} size={18} className={dlqOk ? 'text-emerald-600 dark:text-emerald-300' : 'text-rose-600 dark:text-rose-300'} filled />
        <div className="flex-1">
          <div className="text-[12.5px] font-bold text-neutral-900 dark:text-white">
            {dlqOk ? 'DLQ is clean' : `${dlqCount} unresolved DLQ ${dlqCount === 1 ? 'job' : 'jobs'}`}
          </div>
          <div className="text-[10.5px] text-arena-600 dark:text-neon-200/70">
            {dlqOk ? 'No failed settlement jobs.' : 'Click to retry / resolve.'}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Link to="/settings/replay" className="rounded-xl px-3 py-2 bg-white dark:bg-arena-700/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 hover:bg-arena-50 dark:hover:bg-arena-700/60 transition">
          <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/70">
            <Icon name="restart_alt" size={12} /> Replay
          </div>
          <div className="text-[11.5px] text-arena-700 dark:text-neon-200/80 mt-0.5">Re-run settlement</div>
        </Link>
        <Link to="/settings/reassign" className="rounded-xl px-3 py-2 bg-white dark:bg-arena-700/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 hover:bg-arena-50 dark:hover:bg-arena-700/60 transition">
          <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/70">
            <Icon name="swap_horiz" size={12} /> Reassign
          </div>
          <div className="text-[11.5px] text-arena-700 dark:text-neon-200/80 mt-0.5">Bulk move</div>
        </Link>
      </div>
    </div>
  );
}

function DepositLedgerMini({ rows }: { rows: AgentInsights['depositLedger'] }) {
  if (!rows.length) return <div className="text-center text-[12px] text-arena-500 dark:text-neon-200/45 py-5">No deposit activity yet.</div>;
  return (
    <ul className="space-y-1">
      {rows.slice(0, 5).map((r) => {
        const isIn = r.amount >= 0;
        return (
          <li key={r.id} className="px-2 py-1.5 -mx-2 rounded-lg flex items-center gap-2">
            <span className={`grid place-items-center w-6 h-6 rounded-full shrink-0 ${
              isIn
                ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-400/15 dark:text-emerald-300'
                : 'bg-rose-50 text-rose-600 dark:bg-rose-400/15 dark:text-rose-300'
            }`}>
              <Icon name={isIn ? 'arrow_downward' : 'arrow_upward'} size={13} />
            </span>
            <div className="min-w-0 flex-1 text-[12px]">
              <div className="truncate capitalize text-neutral-900 dark:text-white">{r.kind.replace(/_/g, ' ')}</div>
              <div className="text-[10.5px] text-arena-600 dark:text-neon-200/50">
                {new Date(r.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
              </div>
            </div>
            <div className={`text-[12.5px] font-bold tabular-nums shrink-0 ${isIn ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}>
              {isIn ? '+' : '−'}{inr(Math.abs(r.amount))}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
